import * as turf from '@turf/turf'
import {
  GeoJSONSource,
  Map as MapboxMap,
  MapMouseEvent,
  MapTouchEvent,
} from 'mapbox-gl'
import {
  GeoJSONStoreFeatures,
  HexColor,
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawFreehandLineStringMode,
  TerraDrawLineStringMode,
  TerraDrawMouseEvent,
  TerraDrawPointMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawRenderMode,
  TerraDrawSelectMode,
} from 'terra-draw'
import { TerraDrawMapboxGLAdapter } from 'terra-draw-mapbox-gl-adapter'

import { IGeoJson2DGeographicCoordinates } from '~/client/graph'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'

import { MARKUP_EDIT_LAYER } from '../MapBoxViewer/mapboxConstants'
import { arrowheadChevron, chevronWingPx, dotRadiusPx } from './markupArrowhead'
import { drawCursor } from './markupCursor'
import {
  HANDLE_ACCENT,
  HANDLE_FILL,
  MIDPOINT_RADIUS,
  MIDPOINT_STROKE_WIDTH,
  VERTEX_RADIUS,
  VERTEX_STROKE_WIDTH,
} from './markupHandleStyle'
import { isPointOverLayers } from './markupLayerHit'
import {
  fromPlane,
  IPlanePoint,
  planeCentroid,
  planeRectangle,
  rotatePlane,
  toPlane,
} from './markupPlane'
import {
  ArrowheadType,
  DEFAULT_SHAPE_PX,
  DrawType,
  FillStyle,
  LineType,
  MarkupTool,
  ShapeKind,
  StrokeStyle,
} from './markupTools'

import Colors from '~/client/src/shared/theme.module.scss'

export type {
  ArrowheadType,
  DrawType,
  FillStyle,
  LineType,
  MarkupTool,
  ShapeKind,
  StrokeStyle,
}

export type ArrowheadPosition = 'start' | 'end'

export type MarkupKind =
  | 'shape'
  | 'arrow'
  | 'freehand'
  | 'highlighter'
  | 'text'
  | 'callout'

interface IMarkupStyle {
  color: string
  strokeWidth: number
  opacity: number
  strokeStyle: StrokeStyle
  // Shapes carry an independent fill color + pattern; the stroke uses `color`.
  fillColor: string
  fillStyle: FillStyle
}

export interface IMarkupFeatureData {
  id: string
  kind: MarkupKind
  geometryType: string
  coordinates: IGeoJson2DGeographicCoordinates[]
  color: string
  strokeWidth: number
  opacity: number
  strokeStyle?: StrokeStyle
  fillColor?: string
  fillStyle?: FillStyle
  text?: string
  shapeKind?: ShapeKind
  lineType?: LineType
  arrowheadStart?: ArrowheadType
  arrowheadEnd?: ArrowheadType
  // Text markup fields (kind === 'text'); shared so one DTO round-trips
  // geometry + text through MarkupEditor save/load.
  fontSize?: number
  bold?: boolean
  italic?: boolean
  underline?: boolean
  textAlign?: string
  boxWidth?: number
  rotation?: number
  globeViewId?: string
}

export interface IMarkupSelection {
  id: string
  kind: MarkupKind
  color: string
  strokeWidth: number
  strokeStyle: StrokeStyle
  fillColor: string
  fillStyle: FillStyle
  arrowheadStart: ArrowheadType
  arrowheadEnd: ArrowheadType
  // The border color has been set explicitly by the user — fill changes stop
  // auto-deriving it (FigJam-style fill↔border reflection).
  borderOverridden: boolean
  anchor: { lng: number; lat: number }
  // True only for a selection that has a manual move→edit step (polygon / 3+-point
  // polyline) — i.e. a 2nd click promotes it to vertex edit. False for 2-point lines
  // (which auto-advance) and non-vertex shapes. Drives the "click again" coaching tip.
  vertexEditHint: boolean
}

interface IControllerOptions {
  onChange?: () => void
  onSelectionChange?: (id: string | null) => void
  onToolChange?: (tool: MarkupTool) => void
  // Drives the editor's "Finish shaping" / Trash toolbar.
  onDrawingChange?: (vertexCount: number) => void
  // So the editor can retire the "click again" coaching tip once it's been learned.
  onEnterVertexEdit?: () => void
  // Stamped onto each markup at creation so it stays tied to that globe regardless
  // of later globe/save state.
  getGlobeViewId?: () => string | undefined
  isMobile?: boolean
  // So an overlay's Escape doesn't cancel a half-drawn segment on the map behind it.
  canHandleKeys?: () => boolean
  // The editor's cursor, null for none. The host applies it: the map canvas has one writer.
  onCursorChange?: (cursor: string | null) => void
  // Drawn above a resting markup, so a click on them selects nothing beneath.
  yieldToLayerIds?: string[]
  // Text markup layers to yield to, hit-tested by their own layer.
  yieldToTextLayerIds?: string[]
}

type FeatureId = string | number

interface IFeatureMeta {
  kind: MarkupKind
  style: IMarkupStyle
  text?: string
  shapeKind?: ShapeKind
  lineType?: LineType
  arrowheadStart?: ArrowheadType
  arrowheadEnd?: ArrowheadType
  // Border color explicitly chosen by the user — stops fill→border derivation.
  borderOverridden?: boolean
  // Round-trips via Markup.rotation so the rotate-pin chrome stays correct across
  // save/reload + undo. Presentation metadata only — geometry stays baked into coordinates.
  bboxAngle?: number
  globeViewId?: string
}

// A sentinel string, not null: several load paths fall back `fillColor ?? color`,
// which would silently turn a cleared fill back into a colored one.
export const NO_FILL = 'none'

export const MARKUP_DEFAULT_COLOR = Colors.mapBlack

export const DEFAULT_MARKUP_STYLE: IMarkupStyle = {
  color: MARKUP_DEFAULT_COLOR,
  strokeWidth: 8,
  opacity: 1,
  strokeStyle: 'solid',
  fillColor: NO_FILL,
  fillStyle: 'solid',
}

export const HIGHLIGHTER_OPACITY = 0.4

// Split a 6- or 8-digit hex into a 6-digit color + 0..1 opacity. Shape fills
// carry transparency in the color's alpha channel (8-digit hex); we render the
// fill at full opacity for solid colors and let the alpha drive see-through
// fills. Terra Draw only accepts 6-digit colors, so we feed it color + opacity.
export function splitHexAlpha(hex: string): { color: string; opacity: number } {
  if (hex === NO_FILL) return { color: '#000000', opacity: 0 }
  if (/^#[0-9a-fA-F]{8}$/.test(hex)) {
    return {
      color: hex.slice(0, 7),
      opacity: parseInt(hex.slice(7, 9), 16) / 255,
    }
  }
  return { color: hex, opacity: 1 }
}
const COORDINATE_PRECISION = 9
// A freehand bbox resize can shrink to here but no further, so the stroke stays selectable.
const MIN_FREEHAND_EXTENT_PX = 4
const PREVIEW_SOURCE_ID = 'markup-line-preview'
const PREVIEW_LAYER_ID = MARKUP_EDIT_LAYER.linePreview
const PREVIEW_FILL_SOURCE_ID = 'markup-fill-preview'
const PREVIEW_FILL_LAYER_ID = MARKUP_EDIT_LAYER.fillPreview
const PREVIEW_FILL_OUTLINE_LAYER_ID = MARKUP_EDIT_LAYER.fillPreviewOutline
const ARROWHEAD_SOURCE_ID = 'markup-edit-arrowheads'
const ARROWHEAD_LAYER_ID = MARKUP_EDIT_LAYER.arrowheads
const ARROWHEAD_DOT_SOURCE_ID = 'markup-edit-arrowdots'
const ARROWHEAD_DOT_LAYER_ID = MARKUP_EDIT_LAYER.arrowdots
const STROKE_SOURCE_ID = 'markup-edit-strokes'
const STROKE_SOLID_LAYER_ID = MARKUP_EDIT_LAYER.strokesSolid
const STROKE_DASHED_LAYER_ID = MARKUP_EDIT_LAYER.strokesDashed
// Visible resize handles for the selected rectangle/circle, drawn as a GL layer
// so they paint in lockstep with the shape (HTML handles skew a frame). The
// interactive hit-targets stay in the HTML overlay (see MarkupEditor).
const HANDLE_SOURCE_ID = 'markup-edit-handles'
const HANDLE_LAYER_ID = MARKUP_EDIT_LAYER.handles
// Terra Draw paints coordinate dots for EVERY feature in the mode, so committed
// lines/polygons would also sprout dots. We paint only the active drawing's
// placed vertices in our own layer.
const DRAW_VERTEX_SOURCE_ID = 'markup-draw-vertices'
const DRAW_VERTEX_LAYER_ID = MARKUP_EDIT_LAYER.drawVertices
// Screen-px radius within which a double-click counts as landing on an already
// placed polyline vertex (which closes the polyline into a polygon).
const VERTEX_HIT_PX = 10
// Hot overlay for the markup being actively dragged/resized/rotated. The static
// overlays live in separate GeoJSON sources parsed async in workers, so mid-drag
// their updates can land in different paint frames and visibly split apart;
// painting the whole group from ONE source (one setData per frame) keeps every
// part on the same tile snapshot. (The fill stays Terra Draw's — see activeDragId.)
const ACTIVE_SOURCE_ID = 'markup-active'
const ACTIVE_CASING_LAYER_ID = MARKUP_EDIT_LAYER.activeCasing
const ACTIVE_STROKE_SOLID_LAYER_ID = MARKUP_EDIT_LAYER.activeStrokeSolid
const ACTIVE_STROKE_DASHED_LAYER_ID = MARKUP_EDIT_LAYER.activeStrokeDashed
const ACTIVE_ARROWHEAD_LAYER_ID = MARKUP_EDIT_LAYER.activeArrowheads
const ACTIVE_ARROWDOT_LAYER_ID = MARKUP_EDIT_LAYER.activeArrowdots
const ACTIVE_HANDLE_LAYER_ID = MARKUP_EDIT_LAYER.activeHandles
// A press can land on a vertex/midpoint handle's RIM, just outside the stroke's
// own hit tolerance — count presses this close to an edit-stage vertex as hits,
// or the drag would run without the interaction lifecycle (frozen chrome).
const EDIT_HANDLE_HIT_PX = 12
// Highlight outline traced via line-gap-width (hugs the stroke's edge). Sits
// beneath the fill so a shape's fill masks the inner edge → a single outer
// outline, while lines (no fill) get a clean outline on both edges.
const HOVER_SOURCE_ID = 'markup-edit-hover'
const HOVER_LAYER_ID = MARKUP_EDIT_LAYER.hover
const HOVER_OUTLINE_WIDTH = 2
const HOVER_HIT_MARGIN_PX = 6
// Pointer travel (px) below which a drag is treated as a single click.
const CLICK_THRESHOLD_PX = 4
const CIRCLE_STEPS = 64
// Gap between the rendered stroke's edge and the resize handles it carries.
const CIRCLE_HANDLE_PAD_PX = 3
// Refinement passes for a circle resize, and when a pass is close enough to stop.
const CIRCLE_RESIZE_PASSES = 4
const CIRCLE_RESIZE_TOLERANCE_PX = 0.25
// A resize can shrink to here but no further, so the circle stays grabbable.
const MIN_CIRCLE_RADIUS_PX = 2
// Shift-drag constrains a line/arrow to straight/45° increments (relative to the screen).
const SEGMENT_SNAP_DEG = 45

type CaptureKind = 'freehand' | 'segment' | 'rectangle' | 'circle' | 'eraser'

// Terra Draw writes the canvas cursor itself, and its select mode "unsets" it on every pointer
// move with nothing selected. Routed to the controller's cursor sink instead.
class CursorSinkAdapter extends TerraDrawMapboxGLAdapter {
  public constructor(
    map: MapboxMap,
    private readonly setEditorCursor: (cursor: string | null) => void,
  ) {
    super({ map })
  }

  public setCursor(
    cursor: Parameters<TerraDrawMapboxGLAdapter['setCursor']>[0],
  ): void {
    this.setEditorCursor(cursor === 'unset' ? null : cursor)
  }
}

/**
 * Owns the Terra Draw instance bound to a Mapbox GL map for a markup edit
 * session. Geometry is stored as lng/lat GeoJSON so it stays correct under
 * pan/zoom/tilt. Per-feature color/width/opacity is property-driven via a
 * sidecar keyed by feature id, so each feature keeps its own style. The active
 * tool plus its sub-selection (shape-kind / line-type / draw-type) maps to a
 * Terra Draw mode; the sub-selection is tagged onto each finished feature.
 */
export default class MarkupDrawController {
  private readonly draw: TerraDraw
  private readonly map: MapboxMap
  private readonly modeNames: Record<
    | 'select'
    | 'rectangle'
    | 'circle'
    | 'polygon'
    | 'line'
    | 'freehand'
    | 'point'
    | 'passive',
    string
  >
  private readonly featureMeta = new Map<FeatureId, IFeatureMeta>()

  private currentTool: MarkupTool = 'select'
  private shapeKind: ShapeKind = 'rectangle'
  private lineType: LineType = 'arrow'
  private drawType: DrawType = 'pen'
  private currentStyle: IMarkupStyle = { ...DEFAULT_MARKUP_STYLE }
  // Whether the default border color is user-set — stamped onto new features so
  // their fill→border derivation starts (un)locked accordingly.
  private styleBorderOverridden = false
  private selectedId: string | null = null
  // Two-stage selection. 'move' = whole-object drag, handles hidden; 'edit' =
  // draggable vertices/midpoints. A line/polygon enters 'move' on select; a 2nd
  // click of the same feature advances to 'edit'. `freshSelection` marks the
  // click cycle that just selected a feature, so it isn't read as the 2nd click.
  private selectionStage: 'move' | 'edit' = 'move'
  private freshSelection = false
  // Guards the deselect→reselect that a stage change performs to make Terra Draw
  // regenerate the selection coordinate points: while set, the select/deselect
  // events it fires must skip their normal bookkeeping (selectedId / stage reset).
  private reapplying = false
  private hoveredId: string | null = null
  private hoverFrame: number | null = null
  private hoverPoint: { x: number; y: number } | null = null
  private restyleNonce = 0
  private restyleTimer: ReturnType<typeof setTimeout> = null
  private destroyed = false
  private gesturesBeforeAdapter: {
    dragPan: boolean
    dragRotate: boolean
    boxZoom: boolean
  }
  private capturing = false
  private captureKind: CaptureKind | null = null
  private capturePoints: number[][] = []
  private captureStart: number[] | null = null
  private captureStartPx: { x: number; y: number } | null = null
  private captureEnd: number[] | null = null
  private captureEndPx: { x: number; y: number } | null = null
  private eraserPrevPx: { x: number; y: number } | null = null
  // Line/Arrow click-to-click: the first clicked point (geo + screen) while waiting
  // for the second click. null = no point-to-point in progress (drag or idle).
  private segmentAnchor: number[] | null = null
  private segmentAnchorPx: { x: number; y: number } | null = null
  // Rotation drag session, all in the camera-independent Mercator plane:
  // original coords + pivot + pointer start angle, snapshotted at beginRotate()
  // so each rotateSelectedToPoint() is drift-free and immune to camera moves.
  private rotateOriginal: IPlanePoint[] | null = null
  private rotateIsPolygon = false
  private rotatePivot: IPlanePoint | null = null
  private rotateStartAngle = 0
  private rotateDelta = 0
  // Tracked alongside Terra Draw so "Finish shaping" can rebuild them as an open polyline.
  private polygonPoints: number[][] = []
  // Mirrored from each click to paint draw-time vertex dots: Terra Draw's own
  // in-progress LineString carries a leading duplicate + a live cursor coordinate,
  // so its snapshot is an unreliable vertex source. Cleared on finish/cancel.
  private polylinePoints: number[][] = []
  // Terminal vertex hovered as a close target (0 = first, length-1 = last for
  // polygons), or null — drives the solid-blue "click to close" dot.
  private closeHoverIndex: number | null = null
  // Edit-time "glue the ends to close" session (one endpoint drag): the receiver
  // endpoint coord painted as the blue close-target dot while snapping, plus which
  // endpoint is dragged. Mirrors the creation-time close affordance.
  private editCloseSnap: number[] | null = null
  private closeDrag: { draggedIndex: number; otherCoord: number[] } | null =
    null
  // Multi-click (polyline/polygon) drawing: the controller renders the in-progress
  // outline itself so Shift can snap it. While active, Terra Draw's in-progress
  // stroke/fill is hidden (styleWidth/styleFillOpacity) and `drawCursor` holds the
  // current (snapped-or-raw) cursor — shared by the preview and the next vertex.
  private previewingMultiClick = false
  private drawCursor: number[] | null = null
  // The feature id of an in-flight drag/resize/rotate. While set, the feature's
  // chrome renders from the single hot source (see ACTIVE_SOURCE_ID) and the
  // static overlays exclude it. The FILL stays Terra Draw's: TD only re-bakes a
  // feature's style on a style change (not per geometry frame), so a "suppressed"
  // TD fill never actually disappears mid-drag — duplicating the fill in the hot
  // source stacked two translucent fills and visibly darkened them all drag long.
  private activeDragId: string | null = null

  public constructor(
    map: MapboxMap,
    private readonly options: IControllerOptions = {},
  ) {
    this.map = map
    const fillStyles = {
      fillColor: this.styleFillColor,
      fillOpacity: this.styleFillOpacity,
      outlineColor: this.styleColor,
      outlineWidth: this.styleWidth,
    }
    const lineStyles = {
      lineStringColor: this.styleColor,
      lineStringWidth: this.styleWidth,
    }

    const rectangle = new TerraDrawRectangleMode({ styles: fillStyles })
    const circle = new TerraDrawCircleMode({ styles: fillStyles })
    // Disable Terra Draw's own finish/cancel keys — we drive finish (→ polyline)
    // and cancel ourselves so Enter doesn't auto-close the ring into a polygon.
    const polygon = new TerraDrawPolygonMode({
      // Hide Terra Draw's own in-progress dots (mirrors the line mode) so only our
      // DRAW_VERTEX_LAYER dots show — polygon vertices then match the polyline's.
      styles: {
        ...fillStyles,
        coordinatePointOpacity: 0,
        coordinatePointOutlineOpacity: 0,
        closingPointOpacity: 0,
        closingPointOutlineOpacity: 0,
        snappingPointOpacity: 0,
        snappingPointOutlineOpacity: 0,
      },
      keyEvents: { finish: null, cancel: null },
    })
    // We paint the polyline's draw-time vertex dots ourselves (refreshDrawVertices)
    // so they persist and match our chrome; hide Terra Draw's own in-progress
    // closing/snapping point so it doesn't show a clashing extra dot.
    const line = new TerraDrawLineStringMode({
      styles: {
        ...lineStyles,
        closingPointOpacity: 0,
        closingPointOutlineOpacity: 0,
        snappingPointOpacity: 0,
        snappingPointOutlineOpacity: 0,
      },
      // Default pointerDistance (40px) treats a click near the last vertex as
      // "finish the line", so placing a vertex close to the previous one aborts
      // the draw. Shrink it so every click adds a coordinate; we drive finishing
      // ourselves (Enter / Finish-line button), so disable Terra Draw's own keys.
      pointerDistance: 1,
      keyEvents: { finish: null, cancel: null },
    })
    const freehand = new TerraDrawFreehandLineStringMode({ styles: lineStyles })
    const point = new TerraDrawPointMode({
      styles: {
        pointColor: this.styleColor,
        pointWidth: this.styleWidth,
        pointOutlineColor: Colors.mapWhite as HexColor,
        pointOutlineWidth: 1,
      },
    })
    // Passive mode: active while the pen/highlighter uses custom pointer
    // capture, so Terra Draw doesn't intercept the drag itself.
    const passive = new TerraDrawRenderMode({ modeName: 'passive', styles: {} })

    // Key the select flags by each mode's ACTUAL name (e.g. freehand is
    // 'freehand-linestring', not 'freehandlinestring') — a hand-typed key that
    // doesn't match a registered mode silently makes those features unselectable.
    // Two-stage selection: a line/polygon starts in 'move' (whole-object drag,
    // handles hidden); a 2nd click of the same feature advances to 'edit'
    // (draggable vertices + midpoints visible). A stage change reselects the
    // feature so Terra Draw regenerates the coordinate points — see applyStage /
    // reselectCurrent. Initial = 'move'.
    const select = new TerraDrawSelectMode({
      flags: {
        [line.mode]: this.moveOnlyFlags(),
        [polygon.mode]: this.moveOnlyFlags(),
        [rectangle.mode]: this.moveOnlyFlags(),
        [circle.mode]: this.moveOnlyFlags(),
        [freehand.mode]: this.moveOnlyFlags(),
        [point.mode]: this.moveOnlyFlags(),
      },
      styles: this.selectStyles('move'),
      // The editor owns Escape (steps edit→move→deselect) and Delete; disable
      // Terra Draw's built-in keys so its Escape doesn't also deselect — that
      // would collapse the two-stage step-down into an immediate deselect.
      keyEvents: { deselect: null, delete: null, rotate: null, scale: null },
      // Replaces Terra Draw's defaults wholesale, so every key is spelled out.
      pointerEvents: {
        leftClick: e => !this.isClickYielded(e),
        rightClick: true,
        contextMenu: false,
        onDragStart: true,
        onDrag: true,
        onDragEnd: true,
      },
    })

    this.modeNames = {
      select: select.mode,
      rectangle: rectangle.mode,
      circle: circle.mode,
      polygon: polygon.mode,
      line: line.mode,
      freehand: freehand.mode,
      point: point.mode,
      passive: passive.mode,
    }

    // Terra Draw's adapter reads dragPan/dragRotate here, once, and gates its own
    // setDraggability on that snapshot — so a handler that happens to be disabled at
    // construction can never be turned off again during a drag, and select-mode drags
    // pan the map instead of moving the shape (STX1-1061). Normalise before, restore on
    // destroy, so no host surface's timing can poison the session.
    this.gesturesBeforeAdapter = {
      dragPan: map.dragPan.isEnabled(),
      dragRotate: map.dragRotate.isEnabled(),
      boxZoom: map.boxZoom.isEnabled(),
    }
    map.dragPan.enable()
    map.dragRotate.enable()

    this.draw = new TerraDraw({
      adapter: new CursorSinkAdapter(map, cursor => {
        if (!this.ownsCursor) this.setCursor(cursor)
      }),
      modes: [
        select,
        rectangle,
        circle,
        polygon,
        line,
        freehand,
        point,
        passive,
      ],
      idStrategy: {
        isValidId: (id): id is string =>
          typeof id === 'string' && /^[a-f\d]{24}$/i.test(id),
        getId: () => BsonObjectId.generate(),
      },
    })

    this.draw.on('finish', this.onFinish)
    this.draw.on('change', this.onChange)
    this.draw.on('select', this.onSelect)
    this.draw.on('deselect', this.onDeselect)
  }

  public start(): void {
    // Hover outline goes in first — beneath the Terra Draw fill — so a shape's
    // fill masks the inner half of the outline and only a single OUTER blue
    // outline shows (lines have no fill, so they get a full surrounding outline).
    this.ensureHoverLayer()
    this.draw.start()
    this.ensureOverlayLayers()
    this.raiseSelectionHandles()
    // Caps are geo geometry that tilts/pans with the map on its own, so we only re-fit
    // them when ZOOM changes (to re-apply the zoom-driven size). Tilt/bearing/pan leave
    // them untouched, so the chevron never morphs mid-gesture.
    this.map.on('zoom', this.rebuildArrowheadCaps)
    this.map.on('style.load', this.handleStyleReload)
    this.setTool('select')
  }

  private ensureOverlayLayers(): void {
    this.ensureArrowheadLayer()
    this.ensureStrokeLayer()
    this.ensureHandleLayer()
    this.ensureDrawVertexLayer()
    this.ensureActiveLayer()
  }

  // setStyle() (map-type change) wipes our + terra-draw's layers; re-register them.
  private readonly handleStyleReload = (): void => {
    if (this.destroyed) return
    const wasEnabled = this.draw.enabled
    // Markups only — the raw snapshot also holds TD's transient selection points.
    const markups = wasEnabled
      ? this.draw.getSnapshot().filter(f => this.featureMeta.has(f.id))
      : []
    try {
      if (wasEnabled) this.draw.stop()
    } catch {
      // unregister() removeSource throws on the already-wiped td-* sources
    }
    this.ensureHoverLayer()
    this.draw.start()
    // start() re-registers empty td-* sources but won't re-emit the store; re-add.
    if (markups.length) {
      this.reapplying = true
      try {
        this.draw.clear()
        this.draw.addFeatures(markups)
      } catch {
        // a still-settling style can make setData throw; let the tail run
      } finally {
        this.reapplying = false
      }
    }
    this.ensureOverlayLayers()
    this.raiseSelectionHandles()
    this.refreshArrowheads()
    this.reselectCurrent()
    // Tool listeners survive setStyle; restore only TD's mode or setTool double-binds.
    this.draw.setMode(this.modeForCurrent())
  }

  // Our committed-stroke/arrowhead overlays are added after Terra Draw's layers,
  // so they'd otherwise cover Terra Draw's selection-point (vertex) handles. Lift
  // the point layers back to the top so handles show when an item is selected.
  // (Terra Draw's mapbox adapter names them `<prefix>-point[/-marker]`, default
  // prefix `td`; created upfront on start, so they exist here.)
  private raiseSelectionHandles(): void {
    const handleLayers = ['td-point', 'td-point-marker']
    handleLayers.forEach(id => {
      if (this.map.getLayer(id)) this.map.moveLayer(id)
    })
  }

  public destroy(): void {
    this.destroyed = true
    // Restore, don't blanket-enable: these are the surface's to decide, and an unconditional
    // enable() left a map that asked for none of them with box-zoom switched on. Quick-zoom below
    // is still unconditional — setQuickZoomEnabled owns it, not the surface.
    this.map.boxZoom[
      this.gesturesBeforeAdapter.boxZoom ? 'enable' : 'disable'
    ]()
    this.map.dragPan[
      this.gesturesBeforeAdapter.dragPan ? 'enable' : 'disable'
    ]()
    this.map.dragRotate[
      this.gesturesBeforeAdapter.dragRotate ? 'enable' : 'disable'
    ]()
    this.map.touchZoomRotate._tapDragZoom?.enable()
    this.map.off('zoom', this.rebuildArrowheadCaps)
    this.map.off('style.load', this.handleStyleReload)
    this.disableCapture()
    this.disableHoverTracking()
    this.disableShapeGhost()
    this.disablePolygonTracking()
    this.disablePolylineTracking()
    this.setCursor(null)
    if (this.restyleTimer) clearTimeout(this.restyleTimer)
    this.removePreviewLayer()
    this.removeArrowheadLayer()
    this.removeStrokeLayer()
    this.removeHandleLayer()
    this.removeDrawVertexLayer()
    this.removeActiveLayer()
    this.removeHoverLayer()
    this.draw.off('finish', this.onFinish)
    this.draw.off('change', this.onChange)
    this.draw.off('select', this.onSelect)
    this.draw.off('deselect', this.onDeselect)
    if (this.draw.enabled) this.draw.stop()
    this.featureMeta.clear()
  }

  // Fire-and-forget defer past Terra Draw's synchronous event dispatch. Uncancellable,
  // so the destroyed guard keeps the body from running against a torn-down controller.
  private defer(fn: () => void): void {
    setTimeout(() => {
      if (this.destroyed) return
      fn()
    }, 0)
  }

  public setTool(tool: MarkupTool): void {
    this.currentTool = tool
    this.applyMode()
  }

  public setShapeKind(kind: ShapeKind): void {
    this.shapeKind = kind
    if (this.currentTool === 'shape') this.applyMode()
  }

  public setLineType(type: LineType): void {
    this.lineType = type
    if (this.currentTool === 'arrow') this.applyMode()
  }

  public setDrawType(type: DrawType): void {
    this.drawType = type
    if (this.currentTool === 'draw') this.applyMode()
  }

  public setColor(color: string): void {
    this.currentStyle = { ...this.currentStyle, color }
    this.restyleSelected()
  }

  public setStrokeWidth(strokeWidth: number): void {
    this.currentStyle = { ...this.currentStyle, strokeWidth }
    this.restyleSelected()
    if (this.currentTool === 'draw') this.updateCursor()
  }

  public setStrokeStyle(strokeStyle: StrokeStyle): void {
    this.currentStyle = { ...this.currentStyle, strokeStyle }
    this.restyleSelected()
  }

  public setFillColor(fillColor: string): void {
    this.currentStyle = { ...this.currentStyle, fillColor }
    this.restyleSelected()
  }

  // Mark the DEFAULT border as user-set (or reset it) — new features inherit it.
  public setBorderOverridden(value: boolean): void {
    this.styleBorderOverridden = value
  }

  // Mark the SELECTED feature's border as user-set, so later fill edits on it
  // stop auto-deriving the border.
  public setSelectedBorderOverridden(value: boolean): void {
    if (this.selectedId == null) return
    const meta = this.featureMeta.get(this.selectedId)
    if (meta) meta.borderOverridden = value
  }

  public setFillStyle(fillStyle: FillStyle): void {
    this.currentStyle = { ...this.currentStyle, fillStyle }
    this.restyleSelected()
  }

  // Endpoint caps are a per-feature property edited from the on-element context
  // toolbar (not a tool-level default), so this mutates the selected feature's
  // meta directly and repaints the arrowhead overlay. The shaft is untouched, so
  // no Terra Draw restyle nudge is needed (caps live in our own GL overlay).
  public setArrowhead(position: ArrowheadPosition, type: ArrowheadType): void {
    if (this.selectedId == null) return
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta) return
    this.featureMeta.set(feature.id, {
      ...meta,
      arrowheadStart: position === 'start' ? type : meta.arrowheadStart,
      arrowheadEnd: position === 'end' ? type : meta.arrowheadEnd,
    })
    this.refreshArrowheads()
    this.options.onChange?.()
  }

  // New arrows default to a head at the end; plain lines/polylines start bare.
  // Used at commit time and as the legacy fallback when loading markups saved
  // before per-end caps existed.
  private defaultArrowheads(lineType?: LineType): {
    start: ArrowheadType
    end: ArrowheadType
  } {
    return lineType === 'arrow'
      ? { start: 'none', end: 'arrow' }
      : { start: 'none', end: 'none' }
  }

  /**
   * Replace every geometry markup with the given set — the restore primitive
   * for the editor's snapshot-based undo/redo. (Terra Draw's own undo/redo is
   * off and ignores programmatic adds, so history is owned by MarkupEditor.)
   */
  public replaceAll(markups: IMarkupFeatureData[]): void {
    this.draw.clear()
    this.featureMeta.clear()
    this.selectedId = null
    this.loadMarkups(markups)
  }

  // Re-paint the committed-stroke / arrowhead / handle / outline overlays from
  // the live store. The editor calls this every painted frame while something is
  // selected, so the overlays track a Terra Draw move (which mutates the store
  // live but only emits 'change' on finish).
  public syncSelectionOverlay(): void {
    if (this.activeDragId != null) this.refreshActiveSelection()
    else this.refreshArrowheads()
  }

  // An interaction (body drag / vertex drag / resize / rotate) started on the
  // selected markup: move its chrome into the single hot source so every part
  // updates atomically, and drop it from the static overlays.
  public beginActiveInteraction(): void {
    if (this.selectedId == null || this.activeDragId != null) return
    this.activeDragId = this.selectedId
    this.refreshArrowheads()
    this.refreshActiveSelection()
  }

  public endActiveInteraction(): void {
    if (this.activeDragId == null) return
    this.activeDragId = null
    const source = this.map.getSource(ACTIVE_SOURCE_ID) as GeoJSONSource
    source?.setData({ type: 'FeatureCollection', features: [] })
    this.refreshArrowheads()
  }

  // Rebuild the whole hot group — casing + fill + stroke + arrowheads + resize
  // handles — from the live snapshot in ONE setData.
  private refreshActiveSelection(): void {
    const source = this.map.getSource(ACTIVE_SOURCE_ID) as GeoJSONSource
    if (!source || this.activeDragId == null) return
    const feature = this.snapshotFeature(this.activeDragId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta) {
      source.setData({ type: 'FeatureCollection', features: [] })
      return
    }
    const features: GeoJSON.Feature[] = []
    const withRole = (f: GeoJSON.Feature, role: string): GeoJSON.Feature => ({
      ...f,
      properties: { ...f.properties, role },
    })
    const coords = this.outlineCoords(feature)
    if (coords) {
      features.push({
        type: 'Feature',
        properties: { role: 'casing', width: meta.style.strokeWidth ?? 1 },
        geometry: { type: 'LineString', coordinates: coords },
      })
    }
    const stroke = this.strokeFeature(feature, meta)
    if (stroke) features.push(withRole(stroke, 'stroke'))
    const heads = this.arrowheadFeatures(feature, meta)
    heads.chevrons.forEach(f => features.push(withRole(f, 'arrowhead')))
    heads.dots.forEach(f => features.push(withRole(f, 'arrowdot')))
    const handles: number[][] = this.getSelectedRectangleCorners() ?? []
    handles.forEach(coordinates =>
      features.push({
        type: 'Feature',
        properties: { role: 'handle' },
        geometry: { type: 'Point', coordinates },
      }),
    )
    source.setData({ type: 'FeatureCollection', features })
  }

  public deleteSelected(): void {
    if (this.selectedId == null) return
    this.removeFeature(this.selectedId)
  }

  // Programmatic deselect (Esc). Routes through Terra Draw's 'deselect' event so
  // the editor's onDeselect clears the selection overlay.
  public deselect(): void {
    if (this.selectedId == null) return
    this.draw.deselectFeature(this.selectedId)
  }

  // Add a pre-built markup (its id/coords/style already set by the caller) and
  // select it — used for paste/duplicate. loadMarkups doesn't emit onChange, so
  // fire it here to record the action in the editor's undo/redo history.
  public addAndSelectMarkup(data: IMarkupFeatureData): void {
    // Round to Terra Draw's precision — offset (pasted) coords otherwise get
    // rejected as "excessive precision" and silently dropped.
    const rounded: IMarkupFeatureData = {
      ...data,
      coordinates: data.coordinates.map(c => ({
        longitude: Number(c.longitude.toFixed(COORDINATE_PRECISION)),
        latitude: Number(c.latitude.toFixed(COORDINATE_PRECISION)),
      })),
    }
    this.loadMarkups([rounded])
    if (this.draw.hasFeature(rounded.id)) {
      this.draw.selectFeature(rounded.id, this.modeNames.select)
    }
    this.options.onChange?.()
  }

  /** The selected markup's style + a lng/lat anchor (its centroid) for the
   * on-element context toolbar. */
  public getSelection(): IMarkupSelection | null {
    if (this.selectedId == null) return null
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta) return null
    const [lng, lat] = turf.centroid(feature as turf.AllGeoJSON).geometry
      .coordinates
    const heads = this.defaultArrowheads(meta.lineType)
    return {
      id: this.selectedId,
      kind: meta.kind,
      color: meta.style.color,
      strokeWidth: meta.style.strokeWidth,
      strokeStyle: meta.style.strokeStyle,
      fillColor: meta.style.fillColor ?? meta.style.color,
      fillStyle: meta.style.fillStyle ?? 'solid',
      arrowheadStart: meta.arrowheadStart ?? heads.start,
      arrowheadEnd: meta.arrowheadEnd ?? heads.end,
      borderOverridden: meta.borderOverridden ?? false,
      anchor: { lng, lat },
      vertexEditHint:
        this.isVertexEditable(this.selectedId) &&
        !this.isTwoPointLine(this.selectedId),
    }
  }

  // The selected feature's outline coordinates (lng/lat) — the editor projects
  // these to a screen bbox to anchor the context toolbar above the markup's top.
  public getSelectedCoords(): number[][] | null {
    if (this.selectedId == null) return null
    const feature = this.snapshotFeature(this.selectedId)
    if (!feature) return null
    const g = feature.geometry
    if (g.type === 'LineString') return g.coordinates as number[][]
    if (g.type === 'Polygon') return g.coordinates[0] as number[][]
    if (g.type === 'Point') return [g.coordinates as number[]]
    return null
  }

  // True when the screen point lies on the currently-selected feature — used to
  // tell a markup MOVE (press on the selected item) from a map pan (press on the
  // bare map), so the editor only hides the toolbar for an actual move.
  public hitSelected(px: { x: number; y: number }): boolean {
    if (this.selectedId == null) return false
    if (this.hitTest(px) === this.selectedId) return true
    // hitTest's tolerance (strokeWidth/2 + margin) can be SMALLER than a vertex/
    // midpoint handle's visual radius for thin strokes — a press on a handle's
    // rim must still count as an interaction start, or the drag would run
    // without the interaction lifecycle (frozen chrome until release).
    return this.selectionStage === 'edit' && this.selectedVertexNear(px)
  }

  private selectedVertexNear(px: { x: number; y: number }): boolean {
    const feature = this.snapshotFeature(this.selectedId)
    const coords = feature && this.outlineCoords(feature)
    if (!coords) return false
    const points: number[][] = []
    coords.forEach((c, i) => {
      points.push(c)
      const next = coords[i + 1]
      if (next) points.push([(c[0] + next[0]) / 2, (c[1] + next[1]) / 2])
    })
    return points.some(c => {
      const s = this.map.project(c as [number, number])
      return Math.hypot(s.x - px.x, s.y - px.y) <= EDIT_HANDLE_HIT_PX
    })
  }

  // True while a vertex-editable feature is in the vertex-edit stage (handles
  // shown) — the only state where a long-press should remove a point.
  public get isEditingVertices(): boolean {
    return (
      this.selectionStage === 'edit' &&
      this.selectedId != null &&
      this.isVertexEditable(this.selectedId)
    )
  }

  // Index of the VERTEX (not a midpoint) nearest `px` within the handle hit
  // radius, or null. A polygon ring carries a closing point — drop it so indices
  // map onto the open ring that deleteSelectedVertexNear writes back.
  private vertexIndexNear(px: { x: number; y: number }): number | null {
    const feature = this.snapshotFeature(this.selectedId)
    const coords = feature && this.outlineCoords(feature)
    if (!coords) return null
    const ring =
      feature.geometry.type === 'Polygon' ? coords.slice(0, -1) : coords
    let best: number | null = null
    let bestDist = EDIT_HANDLE_HIT_PX
    ring.forEach((c, i) => {
      const s = this.map.project(c as [number, number])
      const d = Math.hypot(s.x - px.x, s.y - px.y)
      if (d <= bestDist) {
        bestDist = d
        best = i
      }
    })
    return best
  }

  // Touch counterpart to right-click delete: remove the vertex under `px`. No-op
  // (returns false) unless in vertex-edit, a vertex is hit, and the result stays
  // above the per-kind minimum (line ≥ 2, polygon ≥ 3). Mirrors the resize/rotate
  // geometry-write path, then refreshes Terra Draw's edit handles (we bypassed its
  // internal coordinate handler, so re-select + redraw to drop the stale dot).
  public deleteSelectedVertexNear(px: { x: number; y: number }): boolean {
    const id = this.selectedId
    if (!this.isEditingVertices || id == null) return false
    const feature = this.snapshotFeature(id)
    const coords = feature && this.outlineCoords(feature)
    if (!feature || !coords) return false
    const isPolygon = feature.geometry.type === 'Polygon'
    const ring = isPolygon ? coords.slice(0, -1) : coords
    if (ring.length <= (isPolygon ? 3 : 2)) return false
    const index = this.vertexIndexNear(px)
    if (index == null) return false
    const next = ring.filter((_, i) => i !== index)
    this.draw.updateFeatureGeometry(
      id,
      isPolygon
        ? {
            type: 'Polygon',
            coordinates: [this.roundCoords([...next, next[0]])],
          }
        : { type: 'LineString', coordinates: this.roundCoords(next) },
    )
    this.reselectCurrent()
    this.refreshHandles()
    this.refreshOutline()
    this.syncSelectionOverlay()
    this.options.onChange?.()
    return true
  }

  // Glue-to-close (edit): arm when an ENDPOINT of an open polyline (LineString,
  // >= 4 coords so the closed result keeps >= 3 corners) is being dragged in
  // vertex-edit — record which endpoint moves and the stationary other endpoint.
  public beginPolylineClose(px: { x: number; y: number }): boolean {
    this.closeDrag = null
    this.editCloseSnap = null
    if (!this.isEditingVertices || this.selectedId == null) return false
    const feature = this.snapshotFeature(this.selectedId)
    if (!feature || feature.geometry.type !== 'LineString') return false
    const coords = feature.geometry.coordinates as number[][]
    if (coords.length < 4) return false
    const index = this.vertexIndexNear(px)
    const last = coords.length - 1
    if (index !== 0 && index !== last) return false
    this.closeDrag = {
      draggedIndex: index,
      otherCoord: index === 0 ? coords[last] : coords[0],
    }
    return true
  }

  // While dragging: highlight the receiver endpoint blue when the pointer (≈ the
  // dragged endpoint) is within the close tolerance of the stationary endpoint.
  public updatePolylineClose(px: { x: number; y: number }): void {
    if (!this.closeDrag) return
    const s = this.map.project(this.closeDrag.otherCoord as [number, number])
    const snapping = Math.hypot(s.x - px.x, s.y - px.y) <= VERTEX_HIT_PX
    const next = snapping ? this.closeDrag.otherCoord : null
    if (next === this.editCloseSnap) return
    this.editCloseSnap = next
    this.refreshDrawVertices()
  }

  // On release: if snapping, glue the two ends — replace the open polyline with a
  // no-fill polygon (drop the dragged endpoint, close the rest), matching what
  // the creation-time close produces. Deferred a tick so Terra Draw finishes its
  // own vertex drag before we swap the feature.
  public endPolylineClose(): void {
    const session = this.closeDrag
    const snapping = this.editCloseSnap != null
    const id = this.selectedId
    this.closeDrag = null
    this.editCloseSnap = null
    this.refreshDrawVertices()
    if (!session || !snapping || id == null) return
    this.defer(() => {
      if (this.selectedId !== id) return
      const feature = this.snapshotFeature(id)
      const meta = this.featureMeta.get(id)
      if (!feature || feature.geometry.type !== 'LineString' || !meta) return
      const coords = feature.geometry.coordinates as number[][]
      if (coords.length < 4) return
      const rest = coords.filter((_, i) => i !== session.draggedIndex)
      const newId = this.commitFeature(
        this.modeNames.polygon,
        this.polygonGeometry([...rest, rest[0]]),
        {
          kind: 'shape',
          shapeKind: 'polygon',
          style: { ...meta.style, fillColor: NO_FILL },
          bboxAngle: meta.bboxAngle,
        },
      )
      this.removeFeature(id)
      this.finishDiscreteDraw(newId)
    })
  }

  // The selected rectangle's 4 corner coords (ring without the closing point),
  // or null if the selection isn't a rectangle. Used to render custom resize
  // handles.
  public getSelectedRectangleCorners(): number[][] | null {
    if (this.selectedId == null) return null
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta) return null
    if (meta.kind !== 'shape' || meta.shapeKind !== 'rectangle') return null
    if (feature.geometry.type !== 'Polygon') return null
    return (feature.geometry.coordinates[0] as number[][]).slice(0, 4)
  }

  // Resize the selected rectangle by dragging corner `index` to screen point `px`
  // (map-container pixels). The diagonally-opposite corner stays fixed and the
  // rectangle's own perpendicular axes are preserved, so it stays a rectangle
  // (only its extents change) rather than turning into an arbitrary quad.
  public resizeSelectedRectangle(
    index: number,
    px: { x: number; y: number },
  ): void {
    const corners = this.getSelectedRectangleCorners()
    if (!corners || corners.length !== 4 || this.selectedId == null) return
    // All math in the Mercator plane (camera-independent): a plane rectangle
    // has truly perpendicular axes, so resizing keeps it a rectangle at any
    // map pitch/bearing — screen-space axes are sheared under pitch.
    const p = corners.map(c => toPlane(c))
    const drag = toPlane(this.map.unproject([px.x, px.y]).toArray())
    const origin = p[(index + 2) % 4]
    const uPx = p[(index + 1) % 4]
    const vPx = p[(index + 3) % 4]
    const unit = (ax: number, ay: number): { x: number; y: number } => {
      const len = Math.hypot(ax, ay) || 1
      return { x: ax / len, y: ay / len }
    }
    const u = unit(uPx.x - origin.x, uPx.y - origin.y)
    const v = unit(vPx.x - origin.x, vPx.y - origin.y)
    const wx = drag.x - origin.x
    const wy = drag.y - origin.y
    const du = wx * u.x + wy * u.y
    const dv = wx * v.x + wy * v.y
    const at = (sx: number, sy: number): number[] => fromPlane({ x: sx, y: sy })
    const next: number[][] = []
    next[(index + 2) % 4] = at(origin.x, origin.y)
    next[(index + 1) % 4] = at(origin.x + u.x * du, origin.y + u.y * du)
    next[(index + 3) % 4] = at(origin.x + v.x * dv, origin.y + v.y * dv)
    next[index] = at(
      origin.x + u.x * du + v.x * dv,
      origin.y + u.y * du + v.y * dv,
    )
    this.draw.updateFeatureGeometry(this.selectedId, {
      type: 'Polygon',
      coordinates: [this.roundCoords([...next, next[0]])],
    })
    this.syncSelectionOverlay()
    this.options.onChange?.()
  }

  // The selected circle's edge-handle anchor (its first ring vertex, due-east of
  // the centre), or null if the selection isn't a circle. Drives the single
  // radius handle.
  public getSelectedCircleEdge(): number[] | null {
    if (this.selectedId == null) return null
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta) return null
    if (meta.kind !== 'shape' || meta.shapeKind !== 'circle') return null
    if (feature.geometry.type !== 'Polygon') return null
    return (feature.geometry.coordinates[0] as number[][])[0] ?? null
  }

  // Resize the selected circle by dragging a bounding-box corner to screen point `px`; the centre
  // stays fixed. Solved, not scaled: the grabbed corner sits at the projected ring's box PLUS a
  // fixed pixel pad, so with half-extents a·r and b·r its distance from the box centre is
  //
  //   d(r) = hypot(a·r + pad, b·r + pad)
  //
  // which is quadratic in r, not proportional to it. Multiplying the old radius by d_wanted/d_now
  // therefore lands short — by 12px on a small circle with a thick stroke — and, because the pad
  // enters additively while the multiplier is multiplicative, the error never cancels: it persists
  // as a lag for the whole drag rather than converging away. Inverting d(r) puts the corner on the
  // pointer in one step, and leaves the radius untouched when the pointer is already on it.
  //
  // The previous centre→drag/√2 was wrong twice over: it assumed the corner sat on a SQUARE of
  // half-side r (true only unpitched, where a === b) and ignored the pad entirely, so merely
  // pressing a handle jumped the radius ~10% at the default stroke and ~23% at stroke 12.
  public resizeSelectedCircle(
    px: { x: number; y: number },
    index: number,
  ): void {
    if (this.selectedId == null) return
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta || meta.shapeKind !== 'circle') return
    if (feature.geometry.type !== 'Polygon') return
    const ringNow = feature.geometry.coordinates[0] as number[][]
    const pts = ringNow.slice(0, -1)
    if (!pts.length) return
    // Centre in the Mercator plane (camera-independent) — a projected-screen
    // centroid drifts under pitch and turns the circle into an ellipse.
    const centerLL = fromPlane(planeCentroid(pts.map(c => toPlane(c))))
    // ring[0] is the east point, so it sits at exactly the radius.
    const radius = turf.distance(centerLL, pts[0], { units: 'kilometers' })
    if (!radius) return
    const ring = this.circleTrackingPointer(
      centerLL,
      radius,
      meta.style.strokeWidth ?? 0,
      px,
      index,
    )
    if (!ring) return
    this.draw.updateFeatureGeometry(this.selectedId, {
      type: 'Polygon',
      coordinates: [this.roundCoords(ring)],
    })
    this.syncSelectionOverlay()
    this.options.onChange?.()
  }

  // The selected circle's screen-axis-aligned bounding box as 4 geo corners
  // [TL, TR, BR, BL] — the editor projects them for the resize handles + box.
  // Bounds the PROJECTED ring (under map tilt a geo-circle projects to an ellipse,
  // so a fixed-radius square won't fit — take the actual on-screen extremes). A
  // circle has no orientation, so the box is upright at any bearing/pitch; padded
  // to the rendered stroke edge. Null unless a circle.
  public getSelectedCircleCorners(): number[][] | null {
    if (!this.getSelectedCircleEdge() || this.selectedId == null) return null
    const feature = this.snapshotFeature(this.selectedId)
    if (!feature || feature.geometry.type !== 'Polygon') return null
    const ring = feature.geometry.coordinates[0] as number[][]
    if (ring.length < 3) return null
    const meta = this.featureMeta.get(feature.id)
    const { centre, halfX, halfY, pad } = this.circleScreenBox(
      ring,
      meta?.style.strokeWidth ?? 0,
    )
    const minX = centre.x - halfX - pad
    const maxX = centre.x + halfX + pad
    const minY = centre.y - halfY - pad
    const maxY = centre.y + halfY + pad
    return (
      [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY],
      ] as [number, number][]
    ).map(([x, y]) => this.map.unproject([x, y]).toArray())
  }

  // The ring whose grabbed corner comes closest to `pointer`.
  //
  // A circle resize is ONE degree of freedom driven by a two-dimensional pointer, so the corner
  // cannot generally sit under the cursor: as the radius varies it traces a curve across the
  // screen, and the pointer is usually off that curve. What is well defined is the nearest point
  // ON it, which is what this finds — exactly, when the pointer does lie on the curve.
  //
  // It has to be solved against the corner's real screen POSITION, not its distance from the box
  // centre. Under perspective that centre moves as the radius changes — 19px while doubling a
  // medium circle — so matching a distance taken from the old centre reports success with the
  // corner 18px from the cursor, and over 100px for a large one. Position also removes any need to
  // model how the extents grow: each pass measures which way the corner actually travels and steps
  // along it, so nothing assumes the projection is affine.
  private circleTrackingPointer(
    centerLL: number[],
    radius: number,
    strokeWidth: number,
    pointer: { x: number; y: number },
    index: number,
  ): number[][] | null {
    // Corner order is [TL, TR, BR, BL], and the signs are fixed for the whole drag. Picking the
    // corner from the pointer's current quadrant instead looks equivalent — the box is symmetric —
    // but it swaps handles the moment the pointer crosses the centre: a top-left handle dragged
    // inward would shrink the circle, then start GROWING it again as the bottom-right corner took
    // over the solve. Held signs make it shrink to the floor and stay there.
    const signX = index === 1 || index === 2 ? 1 : -1
    const signY = index === 2 || index === 3 ? 1 : -1
    const cornerFor = (
      at: number,
    ): { ring: number[][]; corner: { x: number; y: number } } | null => {
      const ring = this.circleRingGeo(centerLL, at)
      if (!ring) return null
      const { centre, halfX, halfY, pad } = this.circleScreenBox(
        ring,
        strokeWidth,
      )

      return {
        ring,
        corner: {
          x: centre.x + signX * (halfX + pad),
          y: centre.y + signY * (halfY + pad),
        },
      }
    }
    const gapAt = (corner: { x: number; y: number }): number =>
      Math.hypot(pointer.x - corner.x, pointer.y - corner.y)

    // Never below a couple of pixels: a pointer dragged in past the box centre should shrink the
    // circle, where a root-finding form simply had no answer and left it stuck at the pad radius.
    const floor = MIN_CIRCLE_RADIUS_PX * this.groundKmPerPixel()
    let at = Math.max(radius, floor)
    let best: number[][] | null = null
    let bestGap = Infinity

    for (let pass = 0; pass < CIRCLE_RESIZE_PASSES; pass++) {
      const here = cornerFor(at)
      if (!here) break
      const gap = gapAt(here.corner)
      if (gap < bestGap) {
        best = here.ring
        bestGap = gap
      }
      if (gap < CIRCLE_RESIZE_TOLERANCE_PX) break
      // How far the corner travels per unit of radius, measured rather than assumed.
      const step = Math.max(at, floor) * 1e-3
      const ahead = cornerFor(at + step)
      if (!ahead) break
      const ux = (ahead.corner.x - here.corner.x) / step
      const uy = (ahead.corner.y - here.corner.y) / step
      const speed = ux * ux + uy * uy
      if (!speed) break
      const advance =
        ((pointer.x - here.corner.x) * ux + (pointer.y - here.corner.y) * uy) /
        speed
      const next = Math.max(floor, at + advance)
      if (next === at) break
      at = next
    }

    return best
  }

  // The selected circle's on-screen extent: the projected ring's own box — an ellipse's box once
  // the map is pitched, never a square — plus the pad the handles are drawn at. Both the handle
  // geometry and the resize solve read it here, so they cannot drift apart.
  private circleScreenBox(
    ring: number[][],
    strokeWidth: number,
  ): {
    centre: { x: number; y: number }
    halfX: number
    halfY: number
    pad: number
  } {
    const pts = ring.map(c => this.map.project(c as [number, number]))
    const xs = pts.map(p => p.x)
    const ys = pts.map(p => p.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)

    return {
      centre: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
      halfX: (maxX - minX) / 2,
      halfY: (maxY - minY) / 2,
      pad: strokeWidth / 2 + CIRCLE_HANDLE_PAD_PX,
    }
  }

  // The selected freehand/highlighter stroke's LineString points (lng/lat), or
  // null. The editor projects these to build the screen bounding box + handles.
  public getSelectedFreehandPoints(): number[][] | null {
    if (this.selectedId == null) return null
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta) return null
    if (meta.kind !== 'freehand' && meta.kind !== 'highlighter') return null
    if (feature.geometry.type !== 'LineString') return null
    return feature.geometry.coordinates as number[][]
  }

  // Stroke width of the selected feature, so the editor can pad a selection box
  // out to the rendered stroke's edges (the geometry is just the centerline).
  public getSelectedStrokeWidth(): number | null {
    if (this.selectedId == null) return null
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    return meta ? (meta.style.strokeWidth ?? null) : null
  }

  // Stretch-scale the selected freehand stroke by dragging bbox corner `index`
  // (screen px). The diagonally-opposite corner stays fixed; every point is
  // scaled per-axis about it, so the whole stroke stretches with the box.
  public resizeSelectedFreehandBbox(
    index: number,
    px: { x: number; y: number },
  ): void {
    const points = this.getSelectedFreehandPoints()
    if (!points || points.length < 2 || this.selectedId == null) return
    // Work in the stroke's own (rotated) frame IN THE MERCATOR PLANE so the
    // scale is camera-independent: un-rotate by the chrome angle around the
    // plane centroid, run the axis-aligned scale there, rotate back. Screen
    // space shears under pitch and would bake the skew into the geometry.
    const planePts = points.map(c => toPlane(c))
    const centre = planeCentroid(planePts)
    const theta = (this.getSelectedBboxAngle() * Math.PI) / 180
    const framePts = planePts.map(p => rotatePlane(p, centre, -theta))
    const pxFrame = rotatePlane(
      toPlane(this.map.unproject([px.x, px.y]).toArray()),
      centre,
      -theta,
    )
    const xs = framePts.map(p => p.x)
    const ys = framePts.map(p => p.y)
    // Pad to the rendered stroke's edges + margin, matching the box the editor
    // draws, so the handle the user grabs lines up with the resize reference.
    // The pad is a SCREEN size — convert it to plane units at the centroid.
    const padPx = (this.getSelectedStrokeWidth() ?? 0) / 2 + 4
    const pad =
      padPx * this.sampledPlaneUnitsPerPixel(this.centreOnScreen(centre))
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    // The handles sit on the PADDED box, but the points being scaled are the stroke itself, so both
    // ends of the scale have to be stated without the pad: anchor on the stroke's own far extreme,
    // and take the pad off the pointer to get where that stroke edge should end up. Scaling about
    // the padded corner instead left the grabbed corner short by pad*(scale-1) — 10px at stroke 12
    // stretching to 2x — and walked the corner that is supposed to be pinned by the same amount.
    // Bbox corners [TL, TR, BR, BL], so index 1|2 grab the +x side and 2|3 the +y side.
    const grabsMaxX = index === 1 || index === 2
    const grabsMaxY = index === 2 || index === 3
    const origin = {
      x: grabsMaxX ? minX : maxX,
      y: grabsMaxY ? minY : maxY,
    }
    const denomX = (grabsMaxX ? maxX : minX) - origin.x
    const denomY = (grabsMaxY ? maxY : minY) - origin.y
    // A stroke with no extent on EITHER axis is a point: nothing to scale, no scale defined, and
    // epsilon cannot guard it because epsilon is zero too — `0 < 0` is false, and the divisions
    // below would write Infinity/NaN into the geometry.
    const epsilon = Math.hypot(denomX, denomY) * 1e-6
    if (!(epsilon > 0)) return
    // Dragging the pointer onto the pinned corner drives the scale to zero, and a stroke collapsed
    // to a point cannot be selected again to undo it. Hold each axis at a few pixels — but only an
    // axis that was already wider than that, so a naturally thin stroke is never inflated, and only
    // in magnitude, so dragging past the anchor still mirrors.
    const floor =
      MIN_FREEHAND_EXTENT_PX *
      this.sampledPlaneUnitsPerPixel(this.centreOnScreen(centre))
    const held = (scale: number, denom: number): number => {
      const was = Math.abs(denom)
      const next = Math.abs(scale * denom)
      // Already wide enough, or growing: nothing to hold. Growing is checked against the axis's
      // CURRENT extent, not the floor, so a genuinely thin stroke is stretched by the amount asked
      // for rather than snapped up to the floor.
      if (next >= floor || next >= was) return scale

      // Shrinking below the floor stops AT the floor -- or where the axis already is, if it is
      // already below it. That second clause is what makes this hold across a drag: a bare
      // `floor / was` re-admits the raw scale on the next pointermove, once the previous one has
      // left the extent at (or, after 9dp rounding, just under) the floor, and the stroke
      // collapses after all.
      return (Math.sign(scale) || 1) * (Math.min(floor, was) / was)
    }
    // An axis with no extent has no scale, so it is carried through untouched and the stroke
    // stretches along its length only. A perfectly straight stroke is ordinary input — and is also
    // what a pre-floor resize could leave behind — so it must stay resizable. The padded bounds
    // this replaced hid the case entirely by never letting a denominator reach zero.
    const along = (target: number, denom: number): number =>
      Math.abs(denom) < epsilon ? 1 : held(target / denom, denom)
    const scaleX = along(
      pxFrame.x + (grabsMaxX ? -pad : pad) - origin.x,
      denomX,
    )
    const scaleY = along(
      pxFrame.y + (grabsMaxY ? -pad : pad) - origin.y,
      denomY,
    )
    const next = framePts.map(p =>
      fromPlane(
        rotatePlane(
          {
            x: origin.x + (p.x - origin.x) * scaleX,
            y: origin.y + (p.y - origin.y) * scaleY,
          },
          centre,
          theta,
        ),
      ),
    )
    this.draw.updateFeatureGeometry(this.selectedId, {
      type: 'LineString',
      coordinates: this.roundCoords(next),
    })
    this.syncSelectionOverlay()
    this.options.onChange?.()
  }

  // Drag contract: beginRotate() snapshots the ORIGINAL coords + projected
  // centroid once; each rotateSelectedTo(deltaDeg) rotates that snapshot (not
  // the live geometry), so repeated calls don't accumulate drift — mirroring
  // how resizeSelectedRectangle recomputes from the fixed opposite corner.
  // Rotation is screen-space (project → rotate → unproject) and bakes into the
  // coordinates, so persistence needs no extra field.
  public beginRotate(startPx: { x: number; y: number }): boolean {
    this.rotateOriginal = null
    this.rotatePivot = null
    if (this.selectedId == null) return false
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta || meta.shapeKind === 'circle') return false
    const g = feature.geometry
    let ring: number[][] | null = null
    if (g.type === 'Polygon') {
      ring = (g.coordinates[0] as number[][]).slice(0, -1)
      this.rotateIsPolygon = true
    } else if (g.type === 'LineString') {
      ring = g.coordinates as number[][]
      this.rotateIsPolygon = false
    }
    if (!ring || ring.length < 2) return false
    this.rotateOriginal = ring.map(c => toPlane(c))
    this.rotatePivot = planeCentroid(this.rotateOriginal)
    const start = toPlane(this.map.unproject([startPx.x, startPx.y]).toArray())
    this.rotateStartAngle = Math.atan2(
      start.y - this.rotatePivot.y,
      start.x - this.rotatePivot.x,
    )
    return true
  }

  // Rotate the snapshot so it tracks the pointer at `px`: the delta is the
  // pointer's PLANE angle around the plane pivot minus the start angle — an
  // exact rigid rotation at any pitch/bearing (and the pivot can't go stale
  // if the camera moves mid-drag).
  public rotateSelectedToPoint(
    px: { x: number; y: number },
    snap15: boolean,
  ): void {
    if (!this.rotateOriginal || !this.rotatePivot || this.selectedId == null)
      return
    const point = toPlane(this.map.unproject([px.x, px.y]).toArray())
    const angle = Math.atan2(
      point.y - this.rotatePivot.y,
      point.x - this.rotatePivot.x,
    )
    let deltaDeg = ((angle - this.rotateStartAngle) * 180) / Math.PI
    if (snap15) deltaDeg = Math.round(deltaDeg / 15) * 15
    this.rotateDelta = deltaDeg
    const rad = (deltaDeg * Math.PI) / 180
    const next = this.rotateOriginal.map(c =>
      fromPlane(rotatePlane(c, this.rotatePivot, rad)),
    )
    this.draw.updateFeatureGeometry(
      this.selectedId,
      this.rotateIsPolygon
        ? {
            type: 'Polygon',
            coordinates: [this.roundCoords([...next, next[0]])],
          }
        : { type: 'LineString', coordinates: this.roundCoords(next) },
    )
    this.syncSelectionOverlay()
    this.options.onChange?.()
  }

  public endRotate(): void {
    if (this.selectedId != null && this.rotateDelta !== 0) {
      const feature = this.snapshotFeature(this.selectedId)
      const meta = feature && this.featureMeta.get(feature.id)
      if (meta)
        meta.bboxAngle = ((meta.bboxAngle ?? 0) + this.rotateDelta) % 360
    }
    this.rotateOriginal = null
    this.rotatePivot = null
    this.rotateDelta = 0
  }

  // The selection chrome's orientation: accumulated past rotations plus the
  // in-flight drag delta, so the bbox turns live while rotating.
  public getSelectedBboxAngle(): number {
    if (this.selectedId == null) return 0
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    const base = meta?.bboxAngle ?? 0
    return this.rotateOriginal ? base + this.rotateDelta : base
  }

  // The selection's oriented chrome (Mercator-plane bbox rotated by the chrome
  // angle, optionally padded by a SCREEN pixel amount) as GEO points — the
  // editor projects them per-point, so the chrome stays glued to the geometry
  // at any map pitch/bearing. Corners are [TL, TR, BR, BL] in the box's own
  // frame; bottomMid anchors the rotate pin.
  public getSelectedChromeBox(
    padPx = 0,
    thetaOverrideRad?: number,
  ): { corners: number[][]; bottomMid: number[]; centre: number[] } | null {
    const coords = this.getSelectedCoords()
    if (!coords || coords.length < 2) return null
    const planePts = coords.map(c => toPlane(c))
    const centre = planeCentroid(planePts)
    // Paths pass their min-area plane angle; rectangles carry their orientation in
    // the ring itself (derive from the first edge so the chrome box IS the
    // rectangle); everything else tracks the accumulated bbox angle.
    const rectEdge = this.getSelectedRectangleCorners()
    const theta =
      thetaOverrideRad != null
        ? thetaOverrideRad
        : rectEdge
          ? this.downFacedAngle(
              Math.atan2(
                toPlane(rectEdge[1]).y - toPlane(rectEdge[0]).y,
                toPlane(rectEdge[1]).x - toPlane(rectEdge[0]).x,
              ),
              rectEdge[0],
              rectEdge[1],
            )
          : (this.getSelectedBboxAngle() * Math.PI) / 180
    const frame = planePts.map(pt => rotatePlane(pt, centre, -theta))
    const xs = frame.map(pt => pt.x)
    const ys = frame.map(pt => pt.y)
    const pad = padPx
      ? padPx * this.sampledPlaneUnitsPerPixel(this.centreOnScreen(centre))
      : 0
    const minX = Math.min(...xs) - pad
    const maxX = Math.max(...xs) + pad
    const minY = Math.min(...ys) - pad
    const maxY = Math.max(...ys) + pad
    const back = (x: number, y: number): number[] =>
      fromPlane(rotatePlane({ x, y }, centre, theta))
    return {
      corners: [
        back(minX, minY),
        back(maxX, minY),
        back(maxX, maxY),
        back(minX, maxY),
      ],
      bottomMid: back((minX + maxX) / 2, maxY),
      centre: fromPlane(centre),
    }
  }

  // A rectangle takes its chrome angle from its own first edge, so a ring wound the other way turned
  // the box upside down: maxY became the top, and the rotate pin hung ABOVE the shape. Reachable
  // for any rectangle saved from a right-to-left drag before the ring order was made canonical, and
  // still reachable by dragging a resize corner past its opposite. Flip when the edge projects
  // leftward, so the box's own +x always points screen-right and its +y screen-down — the same
  // down-facing the path branch applies in MarkupEditorStore.repositionSelectionBox.
  private downFacedAngle(
    planeAngle: number,
    from: number[],
    to: number[],
  ): number {
    const a = this.map.project(from as [number, number])
    const b = this.map.project(to as [number, number])
    // The vertical tie-break is not hypothetical: at bearing 90 or 270 the first edge runs exactly
    // north-south, so both of its ends share a longitude — and the ring is stored rounded to 9dp,
    // which makes them bit-identical. Without it neither winding is leftward, so neither is
    // normalised and the two disagree by a half turn.
    const reversed = b.x === a.x ? b.y < a.y : b.x < a.x

    return reversed ? planeAngle + Math.PI : planeAngle
  }

  // The min-area edge of the selected PATH (polyline/polygon), chosen in plane (geo)
  // space so it's camera-invariant and stable across pan/zoom/reselect (a screen-space
  // pick flips between near-equal-area orientations). Null for non-paths (< 3 coords).
  private getSelectedPathMinAreaEdge(): {
    coords: number[][]
    planePts: { x: number; y: number }[]
    bestIdx: number
  } | null {
    const coords = this.getSelectedCoords()
    if (!coords || coords.length < 3) return null
    const planePts = coords.map(c => toPlane(c))
    let bestArea = Infinity
    let bestIdx = -1
    for (let i = 0; i < planePts.length; i++) {
      const a = planePts[i]
      const b = planePts[(i + 1) % planePts.length]
      const dx = b.x - a.x
      const dy = b.y - a.y
      const len = Math.hypot(dx, dy)
      if (len < 1e-12) continue
      const cos = dx / len
      const sin = dy / len
      let minU = Infinity
      let maxU = -Infinity
      let minV = Infinity
      let maxV = -Infinity
      for (const p of planePts) {
        const u = p.x * cos + p.y * sin
        const v = -p.x * sin + p.y * cos
        if (u < minU) minU = u
        if (u > maxU) maxU = u
        if (v < minV) minV = v
        if (v > maxV) maxV = v
      }
      const area = (maxU - minU) * (maxV - minV)
      if (area < bestArea) {
        bestArea = area
        bestIdx = i
      }
    }
    return bestIdx < 0 ? null : { coords, planePts, bestIdx }
  }

  // Min-area orientation as a SCREEN angle (the winning edge projected) — used to
  // pick the down-facing seed orientation for the path selection box.
  public getSelectedPathBoxAngle(): number | null {
    const edge = this.getSelectedPathMinAreaEdge()
    if (!edge) return null
    const { coords, bestIdx } = edge
    const a = this.map.project(coords[bestIdx] as [number, number])
    const b = this.map.project(
      coords[(bestIdx + 1) % coords.length] as [number, number],
    )
    return Math.atan2(b.y - a.y, b.x - a.x)
  }

  // Same orientation as a PLANE angle — the box is built in plane space and
  // projected per-point so it stays glued to the geometry under map pitch.
  public getSelectedPathBoxPlaneAngle(): number | null {
    const edge = this.getSelectedPathMinAreaEdge()
    if (!edge) return null
    const { planePts, bestIdx } = edge
    const a = planePts[bestIdx]
    const b = planePts[(bestIdx + 1) % planePts.length]
    return Math.atan2(b.y - a.y, b.x - a.x)
  }

  public getMarkupFeatures(): IMarkupFeatureData[] {
    return this.draw
      .getSnapshot()
      .filter(f => this.featureMeta.has(f.id))
      .map(f => this.toMarkupData(f))
  }

  public loadMarkups(markups: IMarkupFeatureData[]): void {
    const features = markups.map(m => this.toFeature(m))
    markups.forEach(m => {
      const heads = this.defaultArrowheads(m.lineType)
      this.featureMeta.set(m.id, {
        kind: m.kind,
        style: {
          color: m.color,
          strokeWidth: m.strokeWidth,
          opacity: m.opacity,
          strokeStyle: m.strokeStyle ?? 'solid',
          fillColor: m.fillColor ?? m.color,
          fillStyle: m.fillStyle ?? 'solid',
        },
        text: m.text,
        shapeKind: m.shapeKind,
        lineType: m.lineType,
        arrowheadStart: m.arrowheadStart ?? heads.start,
        arrowheadEnd: m.arrowheadEnd ?? heads.end,
        // Reopened markups: treat the saved border as intentional (don't
        // auto-rederive it from a later fill edit). Session-only — not persisted.
        borderOverridden: true,
        bboxAngle: m.rotation ?? 0,
        globeViewId: m.globeViewId,
      })
    })
    if (features.length) this.draw.addFeatures(features)
    this.refreshArrowheads()
  }

  private get isEraser(): boolean {
    return this.currentTool === 'draw' && this.drawType === 'eraser'
  }

  // Tools that draw via the custom pointer-drag capture (press → drag → release)
  // rather than Terra Draw's click interactions: pen/highlighter (freehand) and
  // arrow/line (2-point segment). Polyline keeps Terra Draw's multi-click line.
  private get usesDragCapture(): boolean {
    // All Draw sub-tools drag: pen/highlighter paint a stroke, eraser wipes one.
    if (this.currentTool === 'draw') return true
    if (this.currentTool === 'arrow') return this.lineType !== 'polyline'
    if (this.currentTool === 'shape') return this.shapeKind !== 'polygon'
    return false
  }

  private captureKindForCurrent(): CaptureKind | null {
    if (this.currentTool === 'draw')
      return this.isEraser ? 'eraser' : 'freehand'
    if (this.currentTool === 'arrow' && this.lineType !== 'polyline') {
      return 'segment'
    }
    if (this.currentTool === 'shape' && this.shapeKind === 'rectangle') {
      return 'rectangle'
    }
    if (this.currentTool === 'shape' && this.shapeKind === 'circle') {
      return 'circle'
    }
    return null
  }

  private applyMode(): void {
    if (this.usesDragCapture) this.enableCapture()
    else this.disableCapture()
    // Shift is reserved for straight/45° line snapping WHILE drawing, so disable
    // Mapbox's Shift+drag box-zoom for drawing tools; keep it in Select mode
    // (there Shift is free — the rotate-snap drag starts on overlay handles, not
    // the canvas, so it never triggers box-zoom).
    if (this.currentTool === 'select') {
      this.enableHoverTracking()
      this.map.boxZoom.enable()
    } else {
      this.disableHoverTracking()
      this.map.boxZoom.disable()
    }
    // Rectangle/circle show a default-size ghost that follows the cursor before
    // the click (polygon uses Terra Draw's multi-click, so no ghost).
    if (this.currentTool === 'shape' && this.shapeKind !== 'polygon') {
      this.enableShapeGhost()
    } else {
      this.disableShapeGhost()
    }
    // Polygon: track each clicked vertex so we can finish-as-polyline.
    if (this.currentTool === 'shape' && this.shapeKind === 'polygon') {
      this.enablePolygonTracking()
    } else {
      this.disablePolygonTracking()
    }
    // Polyline: mirror each clicked vertex so we can paint draw-time dots.
    if (this.currentTool === 'arrow' && this.lineType === 'polyline') {
      this.enablePolylineTracking()
    } else {
      this.disablePolylineTracking()
    }
    // Single source of truth — set AFTER the enable/disable calls above so their
    // order can't clobber it: the controller owns the in-progress outline (and the
    // Shift-snap preview) only for the multi-click tools.
    this.previewingMultiClick =
      (this.currentTool === 'shape' && this.shapeKind === 'polygon') ||
      (this.currentTool === 'arrow' && this.lineType === 'polyline')
    this.draw.setMode(this.modeForCurrent())
    this.updateCursor()
    this.refreshDrawVertices()
  }

  // Quick-zoom (one-finger double-tap-drag, touchZoomRotate._tapDragZoom — no public
  // toggle) is read as tap-then-drag and hijacks a feature move / multi-click draw. The
  // editor drives this from its full idle state (geometry + text + tool); idempotent so a
  // repeated toggle never resets the handler's in-flight gesture recognition.
  public setQuickZoomEnabled(enabled: boolean): void {
    const quickZoom = this.map.touchZoomRotate?._tapDragZoom
    if (!quickZoom || quickZoom.isEnabled() === enabled) return
    if (enabled) quickZoom.enable()
    else quickZoom.disable()
  }

  private enablePolygonTracking(): void {
    this.startPreview()
    this.map.on('click', this.onPolygonClick)
    // Hovering the first vertex highlights it as the close target (click it to close
    // the ring), advertising "Finish shaping" — same cue as the polyline.
    this.map.on('mousemove', this.onPolygonHover)
  }

  private disablePolygonTracking(): void {
    this.drawCursor = null
    this.clearPreview()
    this.map.off('click', this.onPolygonClick)
    this.map.off('mousemove', this.onPolygonHover)
    this.closeHoverIndex = null
    this.resetPolygonPoints()
  }

  // Highlight whichever terminal (first OR last) is hovered — clicking either closes
  // the ring — and show the pointer cursor to advertise the close.
  private readonly onPolygonHover = (e: MapMouseEvent): void => {
    this.drawCursor = this.resolveDrawPoint(e)
    const idx = this.nearestCloseVertex(e.point, true)
    this.setCursor(idx != null ? 'pointer' : 'crosshair')
    if (idx !== this.closeHoverIndex) {
      this.closeHoverIndex = idx
      this.refreshDrawVertices()
    }
    this.updatePreview()
  }

  private resetPolygonPoints(): void {
    if (!this.polygonPoints.length) return
    this.polygonPoints = []
    this.options.onDrawingChange?.(0)
    this.refreshDrawVertices()
  }

  // A click while the polygon tool is active places a vertex (Terra Draw adds it
  // too); we mirror the lng/lat so "Finish shaping" can build an open polyline.
  // The closing click (on the first vertex) commits a polygon via onFinish, which
  // clears these points — so the extra push there is harmless.
  private readonly onPolygonClick = (e: MapMouseEvent): void => {
    this.polygonPoints.push(this.resolveDrawPoint(e))
    this.options.onDrawingChange?.(this.polygonPoints.length)
    this.refreshDrawVertices()
  }

  private enablePolylineTracking(): void {
    this.startPreview()
    this.map.on('click', this.onPolylineClick)
    // Hovering the first vertex advertises the close gesture (a single click
    // there closes into a polygon); disable double-click zoom so a stray
    // double-click while placing vertices doesn't zoom the map.
    this.map.on('mousemove', this.onPolylineHover)
    this.map.doubleClickZoom.disable()
  }

  private disablePolylineTracking(): void {
    this.drawCursor = null
    this.clearPreview()
    this.map.off('click', this.onPolylineClick)
    this.map.off('mousemove', this.onPolylineHover)
    // Don't re-enable doubleClickZoom — it's off for the whole editor (the map's
    // baseline), because on touch its double-tap-drag-to-zoom hijacks a select-tap +
    // feature-move-drag into a map zoom. Re-enabling here leaked it on after drawing a
    // polyline/polygon, which is the intermittent "drag zooms instead of moves" bug.
    this.closeHoverIndex = null
    this.resetPolylinePoints()
  }

  private resetPolylinePoints(): void {
    if (!this.polylinePoints.length) return
    this.polylinePoints = []
    this.options.onDrawingChange?.(0)
    this.refreshDrawVertices()
  }

  // Index of the terminal vertex within close range of p — the first, or (for
  // polygons) the last — used to highlight + close on the start/end of a multi-click
  // draw. null until a close is possible (≥ 3 vertices) or out of range.
  private nearestCloseVertex(
    p: { x: number; y: number },
    includeLast: boolean,
  ): number | null {
    const verts = this.inProgressVertices()
    if (verts.length < 3) return null
    const within = (i: number): boolean => {
      const s = this.map.project(verts[i] as [number, number])
      return Math.hypot(s.x - p.x, s.y - p.y) <= VERTEX_HIT_PX
    }
    if (within(0)) return 0
    if (includeLast && within(verts.length - 1)) return verts.length - 1
    return null
  }

  private readonly onPolylineClick = (e: MapMouseEvent): void => {
    // Clicking the first vertex closes the polyline into a polygon, then switches
    // to Select with the polygon selected and draggable.
    if (this.nearestCloseVertex(e.point, false) === 0) {
      const ring = [...this.polylinePoints, this.polylinePoints[0]]
      const id = this.commitFeature(
        this.modeNames.polygon,
        this.polygonGeometry(ring),
        // No fill, matching a normally-drawn shape (SHAPE_DEFAULT_STYLE) — keep
        // the polyline's stroke as the polygon's outline.
        {
          kind: 'shape',
          style: { ...this.effectiveStyle(), fillColor: NO_FILL },
          shapeKind: 'polygon',
        },
      )
      this.closeHoverIndex = null
      this.resetPolylinePoints()
      this.finishDiscreteDraw(id)
      return
    }
    this.polylinePoints.push(this.resolveDrawPoint(e))
    this.options.onDrawingChange?.(this.polylinePoints.length)
    this.refreshDrawVertices()
  }

  // Hovering near the first vertex (once a polygon is possible) highlights it and
  // shows a pointer cursor to advertise "click to close".
  private readonly onPolylineHover = (e: MapMouseEvent): void => {
    this.drawCursor = this.resolveDrawPoint(e)
    // Only the first vertex closes a polyline (into a polygon); the last finishes the
    // open line via Enter / "Finish line", so it's not a close target here.
    const idx = this.nearestCloseVertex(e.point, false)
    this.setCursor(idx != null ? 'pointer' : 'crosshair')
    if (idx !== this.closeHoverIndex) {
      this.closeHoverIndex = idx
      this.refreshDrawVertices()
    }
    this.updatePreview()
  }

  // Finish the in-progress polygon (Enter / "Finish shaping") by closing the ring
  // into a polygon — same result as clicking the first vertex, so the Polygon tool
  // always yields a closed shape. Needs >= 3 vertices; fewer is discarded. Switching
  // to Select cancels Terra Draw's in-progress polygon and selects the new one.
  public finishPolygon(): void {
    const points = this.polygonPoints.slice()
    if (points.length < 3) {
      this.cancelPolygon()
      return
    }
    const id = this.commitFeature(
      this.modeNames.polygon,
      this.polygonGeometry([...points, points[0]]),
      {
        kind: 'shape',
        style: this.effectiveStyle(),
        shapeKind: 'polygon',
      },
    )
    this.resetPolygonPoints()
    this.finishDiscreteDraw(id)
  }

  // Discard the in-progress polygon and return to Select.
  public cancelPolygon(): void {
    this.resetPolygonPoints()
    this.setTool('select')
    this.options.onToolChange?.('select')
  }

  // Finish the in-progress open polyline (Enter / "Finish line"). The placed
  // vertices become the committed LineString; switching to Select cancels Terra
  // Draw's in-progress line and selects the new one.
  public finishPolyline(): void {
    const points = this.polylinePoints.slice()
    if (points.length < 2) {
      this.cancelPolyline()
      return
    }
    const heads = this.defaultArrowheads('polyline')
    const id = this.commitFeature(
      this.modeNames.line,
      this.lineGeometry(points),
      {
        kind: 'arrow',
        style: this.effectiveStyle(),
        lineType: 'polyline',
        arrowheadStart: heads.start,
        arrowheadEnd: heads.end,
      },
    )
    this.resetPolylinePoints()
    this.finishDiscreteDraw(id)
  }

  // Discard the in-progress polyline and return to Select.
  public cancelPolyline(): void {
    this.resetPolylinePoints()
    this.setTool('select')
    this.options.onToolChange?.('select')
  }

  private enableShapeGhost(): void {
    this.map.on('mousemove', this.onShapeGhostMove)
    this.map.on('mouseout', this.onShapeGhostOut)
  }

  private disableShapeGhost(): void {
    this.map.off('mousemove', this.onShapeGhostMove)
    this.map.off('mouseout', this.onShapeGhostOut)
    this.clearPreview()
  }

  private readonly onShapeGhostMove = (e: MapMouseEvent): void => {
    if (this.capturing) return // the live drag preview takes over
    this.renderShapeGhost(e.point)
  }

  private readonly onShapeGhostOut = (): void => {
    if (!this.capturing) this.clearPreview()
  }

  // A default-size ghost at the cursor, showing where a click would place the
  // shape. Per design it's a TRANSPARENT placeholder (outline only) — not the
  // filled shape — so the basemap shows through. Reuses the drag-preview layers
  // (startPreview restores the solid fill once an actual drag begins).
  private renderShapeGhost(px: { x: number; y: number }): void {
    this.startPreview()
    this.map.setPaintProperty(PREVIEW_FILL_LAYER_ID, 'fill-opacity', 0)
    const ring =
      this.shapeKind === 'circle'
        ? this.circleRing(px, { x: px.x + DEFAULT_SHAPE_PX / 2, y: px.y })
        : this.defaultShapeRect(px)
    const fillSource = this.map.getSource(
      PREVIEW_FILL_SOURCE_ID,
    ) as GeoJSONSource
    if (ring) {
      fillSource?.setData({
        type: 'FeatureCollection',
        features: [this.polygonFeature(ring)],
      })
    }
  }

  private readonly setCursor = (cursor: string | null): void => {
    this.options.onCursorChange?.(cursor)
  }

  // Set by the close-vertex hover and the Select tool's hover tracking. Terra Draw's own for
  // them disagree (a 40px close distance; an unset on every move), so they would only flicker.
  private get ownsCursor(): boolean {
    return (
      this.previewingMultiClick ||
      (this.currentTool === 'select' && this.selectedId == null)
    )
  }

  // Only while nothing is selected: a selected markup draws above them.
  private isClickYielded(e: TerraDrawMouseEvent): boolean {
    return (
      this.selectedId == null &&
      isPointOverLayers(
        this.map,
        { x: e.containerX, y: e.containerY },
        this.options.yieldToLayerIds,
        this.options.yieldToTextLayerIds,
      )
    )
  }

  // The Draw tool shows a custom brush cursor (pencil + footprint circle sized to
  // the stroke width); every other tool keeps the map's default cursor.
  private updateCursor(): void {
    if (this.currentTool === 'draw') {
      this.setCursor(
        drawCursor(this.drawType, this.effectiveStyle().strokeWidth),
      )
    } else if (this.currentTool === 'shape' || this.currentTool === 'arrow') {
      // Placing a shape/arrow (incl. polygon): crosshair (a "+").
      this.setCursor('crosshair')
    } else {
      this.setCursor(null)
    }
  }

  private modeForCurrent(): string {
    switch (this.currentTool) {
      case 'shape':
        // rectangle/circle draw via custom bounding-box drag (passive Terra
        // mode); only polygon uses Terra Draw's multi-click polygon.
        return this.shapeKind === 'polygon'
          ? this.modeNames.polygon
          : this.modeNames.passive
      case 'arrow':
        // arrow/line draw via custom 2-point drag (passive Terra mode); only
        // polyline uses Terra Draw's multi-click LineString.
        return this.lineType === 'polyline'
          ? this.modeNames.line
          : this.modeNames.passive
      case 'draw':
        // pen/highlighter/eraser all use custom pointer capture (passive Terra
        // mode) — the eraser is a brush that trims strokes, not a click-select.
        return this.modeNames.passive
      case 'text':
      case 'callout':
        return this.modeNames.point
      default:
        return this.modeNames.select
    }
  }

  private enableCapture(): void {
    this.map.on('mousedown', this.onCaptureDown)
    this.map.on('touchstart', this.onCaptureDown)
  }

  private disableCapture(): void {
    this.map.off('mousedown', this.onCaptureDown)
    this.map.off('touchstart', this.onCaptureDown)
    this.cancelSegmentPtp()
    if (this.capturing) {
      this.endCaptureListeners()
      this.map.dragPan.enable()
      this.resetCapture()
    }
  }

  private resetCapture(): void {
    this.capturing = false
    this.captureKind = null
    this.capturePoints = []
    this.captureStart = null
    this.captureStartPx = null
    this.captureEnd = null
    this.captureEndPx = null
    this.eraserPrevPx = null
    this.map.getContainer().classList.remove('markup-dragging')
  }

  private readonly onCaptureDown = (e: MapMouseEvent | MapTouchEvent): void => {
    const kind = this.captureKindForCurrent()
    if (!kind) return
    const point = [e.lngLat.lng, e.lngLat.lat]
    this.capturing = true
    this.captureKind = kind
    this.captureStart = point
    this.captureEnd = point
    this.captureStartPx = { x: e.point.x, y: e.point.y }
    this.captureEndPx = { x: e.point.x, y: e.point.y }
    this.capturePoints = [point]
    this.map.dragPan.disable()
    // Make every map overlay pass-through for the draw, so a marker/text box under
    // the cursor can't swallow the canvas pointer stream and freeze the stroke.
    this.map.getContainer().classList.add('markup-dragging')
    if (kind === 'eraser') {
      this.eraserPrevPx = { x: e.point.x, y: e.point.y }
      this.eraseAt(e.point, this.eraserRadiusPx())
    } else {
      this.startPreview()
    }
    this.map.on('mousemove', this.onCaptureMove)
    this.map.on('touchmove', this.onCaptureMove)
    this.map.on('mouseup', this.onCaptureUp)
    this.map.on('touchend', this.onCaptureUp)
  }

  private readonly onCaptureMove = (e: MapMouseEvent | MapTouchEvent): void => {
    if (!this.capturing) return
    if (this.captureKind === 'eraser') {
      this.eraseAlong(e.point)
      return
    }
    const point = [e.lngLat.lng, e.lngLat.lat]
    if (this.captureKind === 'freehand') this.capturePoints.push(point)
    else {
      this.captureEnd = point
      this.captureEndPx = { x: e.point.x, y: e.point.y }
      if (
        this.captureKind === 'segment' &&
        this.captureStartPx &&
        this.isShiftHeld(e)
      ) {
        this.captureEnd = this.snapSegmentEnd(this.captureStartPx, e.point)
      }
    }
    this.updatePreview()
  }

  private readonly onCaptureUp = (e: MapMouseEvent | MapTouchEvent): void => {
    this.endCaptureListeners()
    this.map.dragPan.enable()
    const kind = this.captureKind
    if (kind === 'eraser') {
      this.eraserPrevPx = null
      this.options.onChange?.()
    } else {
      this.clearPreview()
      if (kind === 'freehand') this.commitFreehand()
      else if (kind === 'segment') this.handleSegmentUp(e)
      else if (kind === 'rectangle') this.commitRectangle(e)
      else if (kind === 'circle') this.commitCircle(e)
    }
    this.resetCapture()
  }

  private dragMoved(e: MapMouseEvent | MapTouchEvent): boolean {
    const start = this.captureStartPx
    return (
      !!start &&
      Math.hypot(e.point.x - start.x, e.point.y - start.y) > CLICK_THRESHOLD_PX
    )
  }

  private commitFreehand(): void {
    const points = this.capturePoints
    if (points.length < 2) return
    const simplified = turf.simplify(turf.lineString(points), {
      tolerance: 0.000005,
      highQuality: false,
    })
    // Freehand does not auto-select — drawing successive strokes is common.
    this.commitFeature(
      this.modeNames.freehand,
      this.lineGeometry(simplified.geometry.coordinates as number[][]),
      { kind: this.kindForCurrent(), style: this.effectiveStyle() },
    )
  }

  // Line/Arrow finish on pointer-up: a drag commits the segment (press→release); a
  // plain click starts (1st) or completes (2nd) a click-to-click point-to-point.
  private handleSegmentUp(e: MapMouseEvent | MapTouchEvent): void {
    if (this.dragMoved(e)) {
      // A drag always wins → a clean fresh segment; drop any pending anchor.
      this.cancelSegmentPtp()
      if (this.captureStart && this.captureEnd)
        this.commitSegmentPoints(this.captureStart, this.captureEnd)
      return
    }
    if (this.segmentAnchor && this.segmentAnchorPx) {
      // Ignore a second click on (≈) the first point — don't commit a zero-length
      // segment; keep waiting for a real end point (Escape cancels).
      if (
        Math.hypot(
          e.point.x - this.segmentAnchorPx.x,
          e.point.y - this.segmentAnchorPx.y,
        ) < CLICK_THRESHOLD_PX
      )
        return
      // Second click → commit anchor → this point (Shift snaps, like the drag).
      const start = this.segmentAnchor
      const end = this.isShiftHeld(e)
        ? this.snapSegmentEnd(this.segmentAnchorPx, e.point)
        : [e.lngLat.lng, e.lngLat.lat]
      this.cancelSegmentPtp()
      this.commitSegmentPoints(start, end)
    } else if (this.captureStart && this.captureStartPx) {
      // First click → place the start point and wait for the second (rubber-band).
      this.segmentAnchor = this.captureStart
      this.segmentAnchorPx = this.captureStartPx
      this.startPreview()
      this.refreshDrawVertices()
      this.map.on('mousemove', this.onSegmentHover)
      this.map.on('touchmove', this.onSegmentHover)
      window.addEventListener('keydown', this.onSegmentKeyDown)
    }
  }

  private commitSegmentPoints(start: number[], end: number[]): void {
    const heads = this.defaultArrowheads(this.lineType)
    const id = this.commitFeature(
      this.modeNames.line,
      this.lineGeometry([start, end]),
      {
        kind: this.kindForCurrent(),
        style: this.effectiveStyle(),
        lineType: this.lineType,
        arrowheadStart: heads.start,
        arrowheadEnd: heads.end,
      },
    )
    this.finishDiscreteDraw(id)
  }

  // Rubber-band from the placed first point to the cursor while awaiting the second
  // click (Shift snaps); skipped during a drag (the capture owns the preview then).
  private readonly onSegmentHover = (
    e: MapMouseEvent | MapTouchEvent,
  ): void => {
    if (this.capturing || !this.segmentAnchor || !this.segmentAnchorPx) return
    const end = this.isShiftHeld(e)
      ? this.snapSegmentEnd(this.segmentAnchorPx, e.point)
      : [e.lngLat.lng, e.lngLat.lat]
    this.renderSegmentPreview(this.segmentAnchor, end)
  }

  private renderSegmentPreview(start: number[], end: number[]): void {
    const source = this.map.getSource(PREVIEW_SOURCE_ID) as GeoJSONSource
    if (!source) return
    const features: GeoJSON.Feature[] = [this.lineFeature([start, end])]
    if (this.currentTool === 'arrow') {
      const heads = this.defaultArrowheads(this.lineType)
      const width = this.effectiveStyle().strokeWidth
      if (heads.start === 'arrow') {
        const cap = this.chevronCap(end, start, width)
        if (cap) features.push(this.lineFeature(cap))
      }
      if (heads.end === 'arrow') {
        const cap = this.chevronCap(start, end, width)
        if (cap) features.push(this.lineFeature(cap))
      }
    }
    source.setData({ type: 'FeatureCollection', features })
  }

  private readonly onSegmentKeyDown = (e: KeyboardEvent): void => {
    if (this.options.canHandleKeys?.() === false) return
    if (e.key === 'Escape') {
      e.preventDefault()
      this.cancelSegmentPtp()
    }
  }

  private cancelSegmentPtp(): void {
    if (!this.segmentAnchor) return
    this.segmentAnchor = null
    this.segmentAnchorPx = null
    this.map.off('mousemove', this.onSegmentHover)
    this.map.off('touchmove', this.onSegmentHover)
    window.removeEventListener('keydown', this.onSegmentKeyDown)
    this.clearPreview()
    this.refreshDrawVertices()
  }

  private commitRectangle(e: MapMouseEvent | MapTouchEvent): void {
    if (!this.captureStartPx) return
    // Drag defines the size; a plain click drops a default-size square centered
    // on the click point.
    const ring = this.dragMoved(e)
      ? this.rectangleRing(this.captureStartPx, e.point)
      : this.defaultShapeRect(this.captureStartPx)
    const id = this.commitFeature(
      this.modeNames.rectangle,
      this.polygonGeometry(ring),
      { kind: 'shape', style: this.effectiveStyle(), shapeKind: 'rectangle' },
    )
    this.finishDiscreteDraw(id)
  }

  private commitCircle(e: MapMouseEvent | MapTouchEvent): void {
    if (!this.captureStartPx) return
    // Drag sets the radius; a plain click drops a default-radius circle.
    const edge = this.dragMoved(e)
      ? e.point
      : {
          x: this.captureStartPx.x + DEFAULT_SHAPE_PX / 2,
          y: this.captureStartPx.y,
        }
    const ring = this.circleRing(this.captureStartPx, edge)
    if (!ring) return
    const id = this.commitFeature(
      this.modeNames.circle,
      this.polygonGeometry(ring),
      {
        kind: 'shape',
        style: this.effectiveStyle(),
        shapeKind: 'circle',
      },
    )
    this.finishDiscreteDraw(id)
  }

  // A default-size square ring centered on `c` (screen px), for click-to-place.
  private defaultShapeRect(c: { x: number; y: number }): number[][] {
    const h = DEFAULT_SHAPE_PX / 2
    return this.rectangleRing(
      { x: c.x - h, y: c.y - h },
      { x: c.x + h, y: c.y + h },
    )
  }

  // Per spec: after drawing a discrete shape/segment, auto-switch to Select and
  // select the new feature so its properties tool appears immediately.
  private finishDiscreteDraw(selectId?: FeatureId): void {
    this.setTool('select')
    this.options.onToolChange?.('select')
    if (selectId != null && this.draw.hasFeature(selectId)) {
      this.draw.selectFeature(selectId, this.modeNames.select)
    }
  }

  // Built as a TRUE rectangle in the Mercator plane, centred on the drag and with its axes
  // aligned to the current map bearing, so it matches the on-screen drag at pitch 0 and stays a
  // real rectangle under pitch (unprojecting screen corners on a pitched map yields a trapezoid
  // that's permanently baked into the geometry).
  //
  // Sized from the drag in PIXELS through one scale sampled at the MAP CENTRE, not from the two
  // endpoints' ground span. Ground distance per pixel grows with distance from a pitched camera,
  // and that span carried the growth twice over: unequally between the axes, which stretched a
  // square sweep into a footprint up to 2.4x longer than it was wide (STX1-1062), and again with
  // screen position, which made the same sweep 3.7x larger at the top of a pitched viewport than
  // at the bottom. A gesture states a size; where on screen it happened is not part of it. The
  // reference is the map centre rather than a closed-form web-Mercator scale so that the globe
  // projection — which this surface can be toggled to, and which stays a real globe below zoom 6 —
  // is measured by its own transform.
  private rectangleRing(
    start: { x: number; y: number },
    end: { x: number; y: number },
  ): number[][] {
    const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 }
    const scale = this.sampledPlaneUnitsPerPixel(
      this.map.project(this.map.getCenter()),
    )
    const corners = planeRectangle(
      toPlane(this.map.unproject([center.x, center.y]).toArray()),
      (Math.abs(end.x - start.x) / 2) * scale,
      (Math.abs(end.y - start.y) / 2) * scale,
      this.map.getBearing(),
    )

    return [...corners, corners[0]]
  }

  // Plane units per screen pixel SAMPLED at `at`, across the view — the direction pitch does not
  // foreshorten. Camera-dependent on purpose, and used two ways. For a screen distance that has to
  // look right AT a spot, such as a stroke-width pad, sample it there. For sizing a shape from a
  // gesture, sample it at the MAP CENTRE: a reference that does not move with the gesture is what
  // makes the same drag mean the same size wherever on screen it happens, and taking it from the
  // map rather than from a closed-form Mercator scale is what keeps it right on the globe.
  private sampledPlaneUnitsPerPixel(at: { x: number; y: number }): number {
    const span = 16
    const left = toPlane(this.map.unproject([at.x - span / 2, at.y]).toArray())
    const right = toPlane(this.map.unproject([at.x + span / 2, at.y]).toArray())

    return Math.hypot(right.x - left.x, right.y - left.y) / span
  }

  private isShiftHeld(e: MapMouseEvent | MapTouchEvent): boolean {
    const oe = e.originalEvent as MouseEvent | undefined
    return !!oe && oe.shiftKey === true
  }

  // Shift-snap for a line/arrow: constrain the drag end to a straight/45° angle
  // relative to the SCREEN (parallel to the screen edges at any map bearing).
  // Same bearing-frame trick as rectangleRing — quantise the angle in the frame
  // whose axes match the screen. The drag's ground length is preserved.
  private snapSegmentEnd(
    start: { x: number; y: number },
    end: { x: number; y: number },
  ): number[] {
    const s = toPlane(this.map.unproject([start.x, start.y]).toArray())
    const e = toPlane(this.map.unproject([end.x, end.y]).toArray())
    const bearing = (this.map.getBearing() * Math.PI) / 180
    const ef = rotatePlane(e, s, -bearing)
    const dx = ef.x - s.x
    const dy = ef.y - s.y
    const dist = Math.hypot(dx, dy)
    const step = (SEGMENT_SNAP_DEG * Math.PI) / 180
    const angle = Math.round(Math.atan2(dy, dx) / step) * step
    const snapped: IPlanePoint = {
      x: s.x + dist * Math.cos(angle),
      y: s.y + dist * Math.sin(angle),
    }
    return fromPlane(rotatePlane(snapped, s, bearing))
  }

  // Where the next multi-click vertex (and the live preview) lands: with Shift the
  // segment from the last placed vertex snaps to 0/45/90° in the screen frame (the
  // same rule as the line/arrow drag); otherwise the raw cursor lng/lat.
  private resolveDrawPoint(e: MapMouseEvent): number[] {
    const buf = this.inProgressVertices()
    if (this.isShiftHeld(e) && buf.length) {
      const last = this.map.project(buf[buf.length - 1] as [number, number])
      return this.snapSegmentEnd(last, e.point)
    }
    return [e.lngLat.lng, e.lngLat.lat]
  }

  // Center-out: the press point is the fixed center, dragging sets the radius
  // (screen-pixel distance to the pointer). Built as a GEODESIC circle (equal
  // ground distance around the centre) rather than a screen-space ring, so it
  // stays a clean circle when the map is pitched/rotated — a screen ring's far
  // points hit the horizon on `unproject` and render as a distorted ellipse.
  // ring[0] is the east point (bearing 90°) so the radius handle stays on the
  // right (matching its ew-resize cursor).
  //
  // The pixel distance is converted through the camera-free scale, NOT by
  // unprojecting both pointer points: ground distance per pixel grows with depth
  // under pitch, so a geodesic span between them made the same gesture draw a
  // different circle at every screen row and in every direction — up to 3.7x
  // across the viewport at pitch 60 (STX1-1065). Callers that state a size in
  // pixels (the ghost, click-to-place) therefore get one circle everywhere.
  private circleRing(
    center: { x: number; y: number },
    edge: { x: number; y: number },
  ): number[][] | null {
    const radiusPx = Math.hypot(edge.x - center.x, edge.y - center.y)
    const centerLL = this.map.unproject([center.x, center.y]).toArray()

    return this.circleRingGeo(centerLL, radiusPx * this.groundKmPerPixel())
  }

  // Ground km per screen pixel, measured ACROSS the view at the MAP CENTRE — the direction pitch
  // does not foreshorten, at a reference that does not move with the gesture. Two properties come
  // out of that: the same drag states the same size wherever on screen it happens, which is the
  // whole of STX1-1065; and the scale is the MAP'S OWN, so it is right under any projection. A
  // closed-form web-Mercator scale would be wrong on the globe projection, which this surface can
  // be toggled to and which stays a real globe below zoom 6 (mapbox blends to mercator over
  // zoom 5-6), assigning a high-latitude gesture roughly half the ground radius it should have.
  private groundKmPerPixel(): number {
    const span = 16
    const centre = this.map.project(this.map.getCenter())
    const left = this.map.unproject([centre.x - span / 2, centre.y]).toArray()
    const right = this.map.unproject([centre.x + span / 2, centre.y]).toArray()

    return turf.distance(left, right, { units: 'kilometers' }) / span
  }

  private circleRingGeo(
    centerLL: number[],
    radiusKm: number,
  ): number[][] | null {
    if (!radiusKm) return null
    const ring: number[][] = []
    for (let i = 0; i <= CIRCLE_STEPS; i++) {
      const bearing = 90 - (i / CIRCLE_STEPS) * 360
      ring.push(
        turf.destination(centerLL, radiusKm, bearing, {
          units: 'kilometers',
        }).geometry.coordinates,
      )
    }
    return ring
  }

  // Round to Terra Draw's precision (9 dp): it rounds its own drawn geometry,
  // but externally-added features are rejected if they exceed it.
  private roundCoords(coordinates: number[][]): number[][] {
    return coordinates.map(([lng, lat]) => [
      Number(lng.toFixed(COORDINATE_PRECISION)),
      Number(lat.toFixed(COORDINATE_PRECISION)),
    ])
  }

  private centreOnScreen(centre: IPlanePoint): { x: number; y: number } {
    return this.map.project(fromPlane(centre) as [number, number])
  }

  private lineGeometry(coordinates: number[][]): GeoJSON.Geometry {
    return { type: 'LineString', coordinates: this.roundCoords(coordinates) }
  }

  private polygonGeometry(ring: number[][]): GeoJSON.Geometry {
    return { type: 'Polygon', coordinates: [this.roundCoords(ring)] }
  }

  // The eraser brush radius in screen px (half the adjustable eraser thickness).
  private eraserRadiusPx(): number {
    return Math.max(1, this.effectiveStyle().strokeWidth / 2)
  }

  // Erase the capsule swept from the last brush position to `currPx`, sampling at
  // ≤ radius/2 so a fast drag wipes a continuous band (no gaps between circles).
  private eraseAlong(currPx: { x: number; y: number }): void {
    const r = this.eraserRadiusPx()
    const prev = this.eraserPrevPx ?? currPx
    const dx = currPx.x - prev.x
    const dy = currPx.y - prev.y
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) / (r / 2)))
    for (let i = 1; i <= steps; i++) {
      const t = i / steps
      this.eraseAt({ x: prev.x + dx * t, y: prev.y + dy * t }, r)
    }
    this.eraserPrevPx = currPx
  }

  // Partial-trim erase: for each freehand/highlighter stroke, drop the part inside
  // the brush circle (screen px) and replace the feature with its surviving runs.
  private eraseAt(center: { x: number; y: number }, r: number): void {
    let changed = false
    this.draw.getSnapshot().forEach(feature => {
      const meta = this.featureMeta.get(feature.id)
      if (!meta || (meta.kind !== 'freehand' && meta.kind !== 'highlighter')) {
        return
      }
      if (feature.geometry.type !== 'LineString') return
      const px = (feature.geometry.coordinates as number[][]).map(c =>
        this.map.project(c as [number, number]),
      )
      if (!this.bboxNearBrush(px, center, r)) return
      const runs = this.clipPolylineOutsideCircle(px, center, r)
      if (runs.length === 1 && runs[0].length === px.length) return // untouched
      this.draw.removeFeatures([feature.id])
      this.featureMeta.delete(feature.id)
      const mode =
        (feature.properties as { mode?: string })?.mode ??
        this.modeNames.freehand
      runs.forEach(run => {
        if (run.length < 2) return
        const coords = this.roundCoords(
          run.map(p => this.map.unproject([p.x, p.y]).toArray()),
        )
        const id = BsonObjectId.generate()
        // Clone per fragment — a shared meta ref lets editing one corrupt its siblings.
        this.featureMeta.set(id, { ...meta })
        this.draw.addFeatures([
          {
            id,
            type: 'Feature',
            properties: { mode },
            geometry: { type: 'LineString', coordinates: coords },
          } as GeoJSONStoreFeatures,
        ])
      })
      changed = true
    })
    if (changed) this.refreshArrowheads()
  }

  private bboxNearBrush(
    px: { x: number; y: number }[],
    c: { x: number; y: number },
    r: number,
  ): boolean {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    px.forEach(p => {
      minX = Math.min(minX, p.x)
      maxX = Math.max(maxX, p.x)
      minY = Math.min(minY, p.y)
      maxY = Math.max(maxY, p.y)
    })
    return !(
      c.x + r < minX ||
      c.x - r > maxX ||
      c.y + r < minY ||
      c.y - r > maxY
    )
  }

  // Split a projected polyline into the runs that lie OUTSIDE the circle. Each
  // segment is cut at its circle-boundary crossings; sub-intervals whose midpoint
  // is inside are dropped (and break the run), the rest are kept.
  private clipPolylineOutsideCircle(
    path: { x: number; y: number }[],
    c: { x: number; y: number },
    r: number,
  ): { x: number; y: number }[][] {
    const r2 = r * r
    const inside = (p: { x: number; y: number }): boolean =>
      (p.x - c.x) ** 2 + (p.y - c.y) ** 2 <= r2
    const runs: { x: number; y: number }[][] = []
    let run: { x: number; y: number }[] = []
    const pushPt = (p: { x: number; y: number }): void => {
      const last = run[run.length - 1]
      if (!last || last.x !== p.x || last.y !== p.y) run.push(p)
    }
    const breakRun = (): void => {
      if (run.length >= 2) runs.push(run)
      run = []
    }
    for (let i = 0; i < path.length - 1; i++) {
      const a = path[i]
      const b = path[i + 1]
      const cuts = [0, ...this.circleSegmentTs(a, b, c, r), 1]
      for (let k = 0; k < cuts.length - 1; k++) {
        const t0 = cuts[k]
        const t1 = cuts[k + 1]
        const mid = this.lerpPx(a, b, (t0 + t1) / 2)
        if (inside(mid)) {
          breakRun()
        } else {
          pushPt(this.lerpPx(a, b, t0))
          pushPt(this.lerpPx(a, b, t1))
        }
      }
    }
    breakRun()
    return runs
  }

  // Sorted parameters t ∈ (0,1) where segment a→b crosses the circle boundary.
  private circleSegmentTs(
    a: { x: number; y: number },
    b: { x: number; y: number },
    c: { x: number; y: number },
    r: number,
  ): number[] {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const lenSq = dx * dx + dy * dy
    if (lenSq === 0) return []
    const fx = a.x - c.x
    const fy = a.y - c.y
    const halfB = fx * dx + fy * dy
    const disc = halfB * halfB - lenSq * (fx * fx + fy * fy - r * r)
    if (disc <= 0) return []
    const sq = Math.sqrt(disc)
    return [(-halfB - sq) / lenSq, (-halfB + sq) / lenSq]
      .filter(t => t > 0 && t < 1)
      .sort((x, y) => x - y)
  }

  private lerpPx(
    a: { x: number; y: number },
    b: { x: number; y: number },
    t: number,
  ): { x: number; y: number } {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }
  }

  private commitFeature(
    mode: string,
    geometry: GeoJSON.Geometry,
    meta: IFeatureMeta,
  ): FeatureId {
    const id = BsonObjectId.generate()
    // Features drawn on a rotated camera are born tilted in the plane (they
    // align to the screen) — seed the chrome angle with the bearing so the
    // selection box/pin hug the markup as the user drew it.
    this.featureMeta.set(id, {
      ...meta,
      borderOverridden: meta.borderOverridden ?? this.styleBorderOverridden,
      bboxAngle: meta.bboxAngle ?? this.map.getBearing(),
      // Keep a re-committed feature's globe (polyline-close); fresh draws fall back.
      globeViewId: meta.globeViewId ?? this.options.getGlobeViewId?.(),
    })
    this.draw.addFeatures([
      {
        id,
        type: 'Feature',
        properties: { mode },
        geometry,
      } as GeoJSONStoreFeatures,
    ])
    this.refreshArrowheads()
    this.options.onChange?.()
    return id
  }

  private endCaptureListeners(): void {
    this.map.off('mousemove', this.onCaptureMove)
    this.map.off('touchmove', this.onCaptureMove)
    this.map.off('mouseup', this.onCaptureUp)
    this.map.off('touchend', this.onCaptureUp)
  }

  // Live preview of the in-progress stroke (a temporary GL line layer updated
  // each pointer move), since the committed feature is only added on pointer-up.
  // Renders the freehand path or the arrow/line segment + its chevron, so the
  // edit preview matches exactly what gets saved and displayed.
  private startPreview(): void {
    if (!this.map.getSource(PREVIEW_SOURCE_ID)) {
      this.map.addSource(PREVIEW_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      this.map.addLayer({
        id: PREVIEW_LAYER_ID,
        type: 'line',
        source: PREVIEW_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': Colors.brandBlue,
          'line-width': 8,
          'line-opacity': 1,
        },
      })
    }
    if (!this.map.getSource(PREVIEW_FILL_SOURCE_ID)) {
      this.map.addSource(PREVIEW_FILL_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      this.map.addLayer({
        id: PREVIEW_FILL_LAYER_ID,
        type: 'fill',
        source: PREVIEW_FILL_SOURCE_ID,
        paint: { 'fill-color': Colors.brandBlue, 'fill-opacity': 1 },
      })
      this.map.addLayer({
        id: PREVIEW_FILL_OUTLINE_LAYER_ID,
        type: 'line',
        source: PREVIEW_FILL_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': Colors.brandBlue, 'line-width': 8 },
      })
    }
    const style = this.effectiveStyle()
    this.map.setPaintProperty(PREVIEW_LAYER_ID, 'line-color', style.color)
    this.map.setPaintProperty(PREVIEW_LAYER_ID, 'line-width', style.strokeWidth)
    this.map.setPaintProperty(PREVIEW_LAYER_ID, 'line-opacity', style.opacity)
    const fill = splitHexAlpha(style.fillColor ?? style.color)
    this.map.setPaintProperty(PREVIEW_FILL_LAYER_ID, 'fill-color', fill.color)
    this.map.setPaintProperty(
      PREVIEW_FILL_LAYER_ID,
      'fill-opacity',
      fill.opacity,
    )
    this.map.setPaintProperty(
      PREVIEW_FILL_OUTLINE_LAYER_ID,
      'line-color',
      style.color,
    )
    this.map.setPaintProperty(
      PREVIEW_FILL_OUTLINE_LAYER_ID,
      'line-width',
      style.strokeWidth,
    )
    this.updatePreview()
  }

  private updatePreview(): void {
    const lineSource = this.map.getSource(PREVIEW_SOURCE_ID) as GeoJSONSource
    const fillSource = this.map.getSource(
      PREVIEW_FILL_SOURCE_ID,
    ) as GeoJSONSource
    // Multi-click (polyline/polygon): render the in-progress outline ourselves from
    // the placed vertices + the (snapped-or-raw) cursor, so Shift-snap shows live.
    if (this.previewingMultiClick) {
      const chain = this.drawCursor
        ? [...this.inProgressVertices(), this.drawCursor]
        : this.inProgressVertices().slice()
      const lines: GeoJSON.Feature[] = []
      const fills: GeoJSON.Feature[] = []
      if (chain.length >= 2) {
        if (
          this.currentTool === 'shape' &&
          this.shapeKind === 'polygon' &&
          chain.length >= 3
        ) {
          fills.push(this.polygonFeature([...chain, chain[0]]))
        } else {
          lines.push(this.lineFeature(chain))
        }
      }
      lineSource?.setData({ type: 'FeatureCollection', features: lines })
      fillSource?.setData({ type: 'FeatureCollection', features: fills })
      return
    }
    const lineFeatures: GeoJSON.Feature[] = []
    const fillFeatures: GeoJSON.Feature[] = []
    if (this.captureKind === 'freehand' && this.capturePoints.length >= 2) {
      lineFeatures.push(this.lineFeature(this.capturePoints))
    } else if (
      this.captureKind === 'segment' &&
      this.captureStart &&
      this.captureEnd
    ) {
      lineFeatures.push(this.lineFeature([this.captureStart, this.captureEnd]))
      if (this.currentTool === 'arrow') {
        // Only the default 'arrow' caps need a chevron preview; defaults never
        // include 'circle', and the user restyles caps after committing.
        const heads = this.defaultArrowheads(this.lineType)
        const width = this.effectiveStyle().strokeWidth
        if (heads.start === 'arrow') {
          const cap = this.chevronCap(this.captureEnd, this.captureStart, width)
          if (cap) lineFeatures.push(this.lineFeature(cap))
        }
        if (heads.end === 'arrow') {
          const cap = this.chevronCap(this.captureStart, this.captureEnd, width)
          if (cap) lineFeatures.push(this.lineFeature(cap))
        }
      }
    } else if (this.captureStartPx && this.captureEndPx) {
      const ring =
        this.captureKind === 'rectangle'
          ? this.rectangleRing(this.captureStartPx, this.captureEndPx)
          : this.circleRing(this.captureStartPx, this.captureEndPx)
      if (ring) fillFeatures.push(this.polygonFeature(ring))
    }
    lineSource?.setData({ type: 'FeatureCollection', features: lineFeatures })
    fillSource?.setData({ type: 'FeatureCollection', features: fillFeatures })
  }

  private lineFeature(coordinates: number[][]): GeoJSON.Feature {
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates },
    }
  }

  private polygonFeature(ring: number[][]): GeoJSON.Feature {
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [ring] },
    }
  }

  private clearPreview(): void {
    const empty = { type: 'FeatureCollection' as const, features: [] }
    const lineSource = this.map.getSource(PREVIEW_SOURCE_ID) as GeoJSONSource
    const fillSource = this.map.getSource(
      PREVIEW_FILL_SOURCE_ID,
    ) as GeoJSONSource
    lineSource?.setData(empty)
    fillSource?.setData(empty)
  }

  private removePreviewLayer(): void {
    const layers = [
      PREVIEW_LAYER_ID,
      PREVIEW_FILL_LAYER_ID,
      PREVIEW_FILL_OUTLINE_LAYER_ID,
    ]
    layers.forEach(id => {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    })
    const sources = [PREVIEW_SOURCE_ID, PREVIEW_FILL_SOURCE_ID]
    sources.forEach(id => {
      if (this.map.getSource(id)) this.map.removeSource(id)
    })
  }

  // Persistent overlay that draws the chevron arrowhead for every committed
  // arrow feature during the edit session — Terra Draw renders the shaft but
  // not the head, so this keeps the edit view in parity with the display layer.
  private ensureArrowheadLayer(): void {
    if (this.map.getSource(ARROWHEAD_SOURCE_ID)) return
    this.map.addSource(ARROWHEAD_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    this.map.addLayer({
      id: ARROWHEAD_LAYER_ID,
      type: 'line',
      source: ARROWHEAD_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['get', 'opacity'],
      },
    })
    // The 'circle' cap is a Mapbox circle (screen-px → always round under tilt); its
    // radius is the zoom-scaled px value (see dotRadiusPx), recomputed as zoom changes.
    this.map.addSource(ARROWHEAD_DOT_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    this.map.addLayer({
      id: ARROWHEAD_DOT_LAYER_ID,
      type: 'circle',
      source: ARROWHEAD_DOT_SOURCE_ID,
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': ['get', 'radius'],
        'circle-opacity': ['get', 'opacity'],
      },
    })
  }

  // Build a chevron cap as a true V on the ground (lat-corrected geo, no screen
  // projection), so it tilts/pans/scales with the map like the rest of the markup. Its
  // wing length is driven by stroke width × zoom (never the projected segment), so
  // tilt/bearing/rotation/drag don't resize it. Shared by the committed/active overlays
  // and the draw-time segment preview.
  private chevronCap(
    prev: number[],
    tip: number[],
    strokeWidth: number,
  ): number[][] | null {
    const zoom = this.map.getZoom()
    return arrowheadChevron(
      prev,
      tip,
      chevronWingPx(strokeWidth, prev, tip, zoom),
      zoom,
    )
  }

  // The chevron/dot cap features for one arrow-kind feature, shared between the
  // static arrowhead overlay and the hot (active-drag) overlay.
  private arrowheadFeatures(
    feature: GeoJSONStoreFeatures,
    meta: IFeatureMeta,
  ): { chevrons: GeoJSON.Feature[]; dots: GeoJSON.Feature[] } {
    const chevrons: GeoJSON.Feature[] = []
    const dots: GeoJSON.Feature[] = []
    if (meta.kind !== 'arrow' || feature.geometry.type !== 'LineString')
      return { chevrons, dots }
    const coords = feature.geometry.coordinates as number[][]
    if (coords.length < 2) return { chevrons, dots }
    const heads = this.defaultArrowheads(meta.lineType)
    const cap = (type: ArrowheadType, prev: number[], tip: number[]): void => {
      const stroke = splitHexAlpha(meta.style.color)
      if (type === 'arrow') {
        const chevron = this.chevronCap(prev, tip, meta.style.strokeWidth)
        if (chevron) {
          chevrons.push({
            type: 'Feature',
            properties: {
              color: stroke.color,
              width: meta.style.strokeWidth,
              opacity: (meta.style.opacity ?? 1) * stroke.opacity,
            },
            geometry: { type: 'LineString', coordinates: chevron },
          })
        }
      } else if (type === 'circle') {
        const radius = dotRadiusPx(
          meta.style.strokeWidth,
          prev,
          tip,
          this.map.getZoom(),
        )
        if (radius > 0) {
          dots.push({
            type: 'Feature',
            properties: {
              color: stroke.color,
              radius,
              opacity: (meta.style.opacity ?? 1) * stroke.opacity,
            },
            geometry: { type: 'Point', coordinates: tip },
          })
        }
      }
    }
    cap(meta.arrowheadStart ?? heads.start, coords[1], coords[0])
    cap(
      meta.arrowheadEnd ?? heads.end,
      coords[coords.length - 2],
      coords[coords.length - 1],
    )
    return { chevrons, dots }
  }

  private refreshArrowheads(): void {
    const source = this.map.getSource(ARROWHEAD_SOURCE_ID) as GeoJSONSource
    const dotSource = this.map.getSource(
      ARROWHEAD_DOT_SOURCE_ID,
    ) as GeoJSONSource
    if (!source || !dotSource) return
    this.rebuildArrowheadCaps()
    this.refreshStrokes()
    this.refreshHandles()
    this.refreshOutline()
  }

  // Rebuild just the (zoom-sized) chevron + dot caps. Run on every 'zoom' frame so both
  // caps re-size with zoom; the caps are geo geometry, so tilt/bearing/pan reproject them
  // with the map on their own. Iterates featureMeta + per-feature snapshot (no
  // whole-store deep copy), so it's safe per frame.
  private readonly rebuildArrowheadCaps = (): void => {
    const source = this.map.getSource(ARROWHEAD_SOURCE_ID) as GeoJSONSource
    const dotSource = this.map.getSource(
      ARROWHEAD_DOT_SOURCE_ID,
    ) as GeoJSONSource
    if (!source || !dotSource) return
    const features: GeoJSON.Feature[] = []
    const dots: GeoJSON.Feature[] = []
    this.featureMeta.forEach((meta, id) => {
      if (String(id) === this.activeDragId) return // hot overlay owns it
      const feature = this.snapshotFeature(String(id))
      if (!feature) return
      const heads = this.arrowheadFeatures(feature, meta)
      features.push(...heads.chevrons)
      dots.push(...heads.dots)
    })
    source.setData({ type: 'FeatureCollection', features })
    dotSource.setData({ type: 'FeatureCollection', features: dots })
  }

  private removeArrowheadLayer(): void {
    const layers = [ARROWHEAD_LAYER_ID, ARROWHEAD_DOT_LAYER_ID]
    layers.forEach(id => {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    })
    const sources = [ARROWHEAD_SOURCE_ID, ARROWHEAD_DOT_SOURCE_ID]
    sources.forEach(id => {
      if (this.map.getSource(id)) this.map.removeSource(id)
    })
  }

  // Edit-mode stroke overlay — paints EVERY committed line/polygon-outline stroke
  // ourselves (Terra Draw's stroke is hidden via styleWidth → 0). Direct
  // source.setData renders synchronously every change, so width/color/style are
  // live (no Terra Draw render batching), and dashing works (Terra Draw has no
  // dash). Solid and dashed split into two layers since line-dasharray is not
  // data-driven. Mirrors the display layer's stroke rendering.
  private ensureStrokeLayer(): void {
    if (this.map.getSource(STROKE_SOURCE_ID)) return
    this.map.addSource(STROKE_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    this.map.addLayer({
      id: STROKE_SOLID_LAYER_ID,
      type: 'line',
      source: STROKE_SOURCE_ID,
      filter: ['!=', ['get', 'dashed'], true],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['get', 'opacity'],
      },
    })
    this.map.addLayer({
      id: STROKE_DASHED_LAYER_ID,
      type: 'line',
      source: STROKE_SOURCE_ID,
      filter: ['==', ['get', 'dashed'], true],
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['get', 'opacity'],
        'line-dasharray': [2, 2],
      },
    })
  }

  private refreshStrokes(): void {
    const source = this.map.getSource(STROKE_SOURCE_ID) as GeoJSONSource
    if (!source) return
    const features: GeoJSON.Feature[] = []
    this.draw.getSnapshot().forEach(feature => {
      if (String(feature.id) === this.activeDragId) return // hot overlay owns it
      const meta = this.featureMeta.get(feature.id)
      if (!meta) return // only committed markups
      const stroke = this.strokeFeature(feature, meta)
      if (stroke) features.push(stroke)
    })
    source.setData({ type: 'FeatureCollection', features })
  }

  // The painted-stroke feature for one committed line/polygon, shared between the
  // static stroke overlay and the hot (active-drag) overlay.
  private strokeFeature(
    feature: GeoJSONStoreFeatures,
    meta: IFeatureMeta,
  ): GeoJSON.Feature | null {
    const coords = this.outlineCoords(feature)
    if (!coords) return null
    const stroke = splitHexAlpha(meta.style.color)
    return {
      type: 'Feature',
      properties: {
        color: stroke.color,
        width: meta.style.strokeWidth,
        opacity: (meta.style.opacity ?? 1) * stroke.opacity,
        dashed: meta.style.strokeStyle === 'dashed',
      },
      geometry: { type: 'LineString', coordinates: coords },
    }
  }

  // Single-feature snapshot lookup. getSnapshot() deep-copies the WHOLE store
  // (every feature, every coordinate) — far too heavy for the per-frame chrome
  // paths, where it ran several times per pointer event and caused visible
  // freezes during drags; getSnapshotFeature copies only the one feature.
  private snapshotFeature(id: string | null): GeoJSONStoreFeatures | null {
    return id == null ? null : (this.draw.getSnapshotFeature(id) ?? null)
  }

  private outlineCoords(feature: GeoJSONStoreFeatures): number[][] | null {
    const geometry = feature.geometry
    let coords: number[][] | null = null
    if (geometry.type === 'LineString') coords = geometry.coordinates
    else if (geometry.type === 'Polygon')
      coords = geometry.coordinates[0] as number[][]
    return coords && coords.length >= 2 ? coords : null
  }

  private removeStrokeLayer(): void {
    const layers = [STROKE_SOLID_LAYER_ID, STROKE_DASHED_LAYER_ID]
    layers.forEach(id => {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    })
    if (this.map.getSource(STROKE_SOURCE_ID))
      this.map.removeSource(STROKE_SOURCE_ID)
  }

  private ensureHandleLayer(): void {
    if (this.map.getSource(HANDLE_SOURCE_ID)) return
    this.map.addSource(HANDLE_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    // Resize handles are circles (white fill, primary ring) — the one primitive
    // Mapbox renders screen-constant and synchronously, so they stay glued to the
    // shape and survive any zoom/bearing/pitch with no rebuild.
    this.map.addLayer({
      id: HANDLE_LAYER_ID,
      type: 'circle',
      source: HANDLE_SOURCE_ID,
      paint: {
        'circle-radius': VERTEX_RADIUS,
        'circle-color': HANDLE_FILL,
        'circle-stroke-color': HANDLE_ACCENT,
        'circle-stroke-width': VERTEX_STROKE_WIDTH,
      },
    })
  }

  // Paint the visible resize handles for the selected rectangle (4 corners) or
  // circle (1 radius handle). Polygons/lines use Terra Draw's own vertex handles,
  // so this stays empty for them. Runs on every geometry change + select/deselect
  // (it shares the stroke overlay's GeoJSON cadence, so handles stay glued to the
  // shape during move/resize).
  private refreshHandles(): void {
    const source = this.map.getSource(HANDLE_SOURCE_ID) as GeoJSONSource
    if (!source) return
    // During an interaction the handles render from the hot overlay instead, so
    // they can't drift from the moving shape. (Circles use HTML corner handles —
    // see MarkupEditor — so the GL handle layer serves only the rectangle.)
    const points: number[][] = this.activeDragId
      ? []
      : (this.getSelectedRectangleCorners() ?? [])
    source.setData({
      type: 'FeatureCollection',
      features: points.map(coordinates => ({
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates },
      })),
    })
  }

  private removeHandleLayer(): void {
    if (this.map.getLayer(HANDLE_LAYER_ID))
      this.map.removeLayer(HANDLE_LAYER_ID)
    if (this.map.getSource(HANDLE_SOURCE_ID))
      this.map.removeSource(HANDLE_SOURCE_ID)
  }

  private ensureDrawVertexLayer(): void {
    if (this.map.getSource(DRAW_VERTEX_SOURCE_ID)) return
    this.map.addSource(DRAW_VERTEX_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    this.map.addLayer({
      id: DRAW_VERTEX_LAYER_ID,
      type: 'circle',
      source: DRAW_VERTEX_SOURCE_ID,
      // Higher sort-key paints on top: keep the first vertex (the close target) and
      // the most-recent one above older neighbours, so a vertex placed close to
      // another never buries them (see refreshDrawVertices).
      layout: { 'circle-sort-key': ['get', 'sortKey'] },
      paint: {
        // The close-target vertex (first vertex, while hovered) grows and fills
        // solid blue to advertise "click to close"; the rest stay small hollow
        // blue-ringed dots.
        'circle-radius': ['case', ['get', 'closeTarget'], 7, VERTEX_RADIUS],
        'circle-color': [
          'case',
          ['get', 'closeTarget'],
          HANDLE_ACCENT,
          HANDLE_FILL,
        ],
        'circle-stroke-color': HANDLE_ACCENT,
        'circle-stroke-width': VERTEX_STROKE_WIDTH,
      },
    })
  }

  // Paint a dot at each PLACED vertex of the in-progress polyline/polygon, so the
  // first click is visible and every vertex persists while drawing (Terra Draw
  // shows none by default, and its own coordinate points would leak onto every
  // committed line in the mode). Cleared whenever no multi-click draw is active.
  private refreshDrawVertices(): void {
    const source = this.map.getSource(DRAW_VERTEX_SOURCE_ID) as GeoJSONSource
    if (!source) return
    const vertices = this.inProgressVertices()
    // The hovered terminal vertex (first, or for polygons the last) is the close
    // target — paint it as the enlarged "click to close" dot. closeHoverIndex is only
    // set when hovering near a terminal during a multi-click draw.
    const features: GeoJSON.Feature[] = vertices.map((coordinates, i) => ({
      type: 'Feature',
      properties: {
        closeTarget: i === this.closeHoverIndex,
        // First vertex (close target) on top, then most-recent over older ones.
        sortKey: i === 0 ? vertices.length + 1 : i,
      },
      geometry: { type: 'Point', coordinates },
    }))
    // Edit-time glue-to-close: paint the receiver endpoint as the same blue dot
    // while an endpoint drag is snapping (no in-progress draw is active then).
    if (this.editCloseSnap) {
      features.push({
        type: 'Feature',
        properties: { closeTarget: true, sortKey: vertices.length + 2 },
        geometry: { type: 'Point', coordinates: this.editCloseSnap },
      })
    }
    source.setData({ type: 'FeatureCollection', features })
  }

  // The placed vertices of the active multi-click draw. Both polygon and polyline
  // clicks are mirrored into their own buffers (one clean lng/lat per click), so
  // there's no Terra Draw cursor/duplicate coordinate to filter out.
  private inProgressVertices(): number[][] {
    if (this.currentTool === 'shape' && this.shapeKind === 'polygon') {
      return this.polygonPoints
    }
    if (this.currentTool === 'arrow' && this.lineType === 'polyline') {
      return this.polylinePoints
    }
    // Line/Arrow click-to-click: show a dot at the placed first point.
    if (this.segmentAnchor) return [this.segmentAnchor]
    return []
  }

  private removeDrawVertexLayer(): void {
    if (this.map.getLayer(DRAW_VERTEX_LAYER_ID))
      this.map.removeLayer(DRAW_VERTEX_LAYER_ID)
    if (this.map.getSource(DRAW_VERTEX_SOURCE_ID))
      this.map.removeSource(DRAW_VERTEX_SOURCE_ID)
  }

  // One source, many layers: every chrome piece of the actively-dragged feature
  // reads from ACTIVE_SOURCE_ID (discriminated by a `role` property), so a single
  // setData updates them all atomically. Paint mirrors the static layers; layer
  // order mirrors the static z-order (casing under fill, stroke/heads/handles on
  // top). The group is added last, so a dragged markup renders above the rest.
  private ensureActiveLayer(): void {
    if (this.map.getSource(ACTIVE_SOURCE_ID)) return
    this.map.addSource(ACTIVE_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    this.map.addLayer({
      id: ACTIVE_CASING_LAYER_ID,
      type: 'line',
      source: ACTIVE_SOURCE_ID,
      filter: ['==', ['get', 'role'], 'casing'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': Colors.brandBlue,
        'line-gap-width': ['get', 'width'],
        'line-width': HOVER_OUTLINE_WIDTH,
      },
    })
    this.map.addLayer({
      id: ACTIVE_STROKE_SOLID_LAYER_ID,
      type: 'line',
      source: ACTIVE_SOURCE_ID,
      filter: [
        'all',
        ['==', ['get', 'role'], 'stroke'],
        ['!=', ['get', 'dashed'], true],
      ],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['get', 'opacity'],
      },
    })
    this.map.addLayer({
      id: ACTIVE_STROKE_DASHED_LAYER_ID,
      type: 'line',
      source: ACTIVE_SOURCE_ID,
      filter: [
        'all',
        ['==', ['get', 'role'], 'stroke'],
        ['==', ['get', 'dashed'], true],
      ],
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['get', 'opacity'],
        'line-dasharray': [2, 2],
      },
    })
    this.map.addLayer({
      id: ACTIVE_ARROWHEAD_LAYER_ID,
      type: 'line',
      source: ACTIVE_SOURCE_ID,
      filter: ['==', ['get', 'role'], 'arrowhead'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['get', 'opacity'],
      },
    })
    this.map.addLayer({
      id: ACTIVE_ARROWDOT_LAYER_ID,
      type: 'circle',
      source: ACTIVE_SOURCE_ID,
      filter: ['==', ['get', 'role'], 'arrowdot'],
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': ['get', 'radius'],
        'circle-opacity': ['get', 'opacity'],
      },
    })
    // Same circle as the cold handle layer, so resize handles look identical
    // whether the shape is idle or mid-interaction (and stay glued).
    this.map.addLayer({
      id: ACTIVE_HANDLE_LAYER_ID,
      type: 'circle',
      source: ACTIVE_SOURCE_ID,
      filter: ['==', ['get', 'role'], 'handle'],
      paint: {
        'circle-radius': VERTEX_RADIUS,
        'circle-color': HANDLE_FILL,
        'circle-stroke-color': HANDLE_ACCENT,
        'circle-stroke-width': VERTEX_STROKE_WIDTH,
      },
    })
  }

  private removeActiveLayer(): void {
    const layers = [
      ACTIVE_CASING_LAYER_ID,
      ACTIVE_STROKE_SOLID_LAYER_ID,
      ACTIVE_STROKE_DASHED_LAYER_ID,
      ACTIVE_ARROWHEAD_LAYER_ID,
      ACTIVE_ARROWDOT_LAYER_ID,
      ACTIVE_HANDLE_LAYER_ID,
    ]
    layers.forEach(id => {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    })
    if (this.map.getSource(ACTIVE_SOURCE_ID))
      this.map.removeSource(ACTIVE_SOURCE_ID)
  }

  private ensureHoverLayer(): void {
    if (this.map.getSource(HOVER_SOURCE_ID)) return
    this.map.addSource(HOVER_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    this.map.addLayer({
      id: HOVER_LAYER_ID,
      type: 'line',
      source: HOVER_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': Colors.brandBlue,
        // Trace the geometry: line-gap-width = the feature's own stroke width, so
        // the 2px outline hugs each edge of the stroke instead of a solid band
        // under it (dashed lines keep their gaps). For shapes the inner edge sits
        // over the fill and is masked (layer is beneath the fill) → single outer.
        'line-gap-width': ['get', 'width'],
        'line-width': HOVER_OUTLINE_WIDTH,
      },
    })
  }

  private removeHoverLayer(): void {
    if (this.map.getLayer(HOVER_LAYER_ID)) this.map.removeLayer(HOVER_LAYER_ID)
    if (this.map.getSource(HOVER_SOURCE_ID))
      this.map.removeSource(HOVER_SOURCE_ID)
  }

  private enableHoverTracking(): void {
    // Touch has no hover: a tap's emulated mousemove would set a sticky hover outline.
    if (!this.options.isMobile) {
      this.map.on('mousemove', this.onHoverMove)
      this.map.on('mouseout', this.onHoverOut)
    }
    this.map.on('click', this.onSelectClick)
    this.map
      .getCanvas()
      .addEventListener('pointerdown', this.onSelectPointerDown)
  }

  private disableHoverTracking(): void {
    this.map.off('mousemove', this.onHoverMove)
    this.map.off('mouseout', this.onHoverOut)
    this.map.off('click', this.onSelectClick)
    this.map
      .getCanvas()
      .removeEventListener('pointerdown', this.onSelectPointerDown)
    if (this.hoverFrame != null) {
      cancelAnimationFrame(this.hoverFrame)
      this.hoverFrame = null
    }
    this.setHovered(null)
  }

  // Throttle the hit-test to one per frame (mousemove fires far more often).
  private readonly onHoverMove = (e: MapMouseEvent): void => {
    this.hoverPoint = { x: e.point.x, y: e.point.y }
    if (this.hoverFrame != null) return
    this.hoverFrame = requestAnimationFrame(() => {
      this.hoverFrame = null
      if (this.hoverPoint) this.setHovered(this.hitTest(this.hoverPoint))
    })
  }

  private readonly onHoverOut = (): void => this.setHovered(null)

  // The cursor moved onto something drawn above the geometry (resting text): no outline under it.
  public clearHover(): void {
    this.setHovered(null)
  }

  private setHovered(id: string | null): void {
    // The selected feature already shows the outline; don't double it on hover.
    const next = id === this.selectedId ? null : id
    if (next === this.hoveredId) return
    this.hoveredId = next
    this.refreshOutline()
  }

  // Topmost committed feature under the cursor (screen-space): point-in-polygon
  // for fills, pixel distance to the stroke for lines. Last match wins (drawn on
  // top). Terra Draw's adapter has no queryRenderedFeatures, so this is manual.
  private hitTest(px: { x: number; y: number }): string | null {
    let hit: string | null = null
    this.draw.getSnapshot().forEach(feature => {
      const meta = this.featureMeta.get(feature.id)
      if (!meta) return
      const geometry = feature.geometry
      const tolerance = (meta.style.strokeWidth ?? 1) / 2 + HOVER_HIT_MARGIN_PX
      if (geometry.type === 'Polygon') {
        const ring = (geometry.coordinates[0] as number[][]).map(c =>
          this.map.project(c as [number, number]),
        )
        if (
          this.pointInRing(px, ring) ||
          this.distanceToPath(px, ring) < tolerance
        ) {
          hit = String(feature.id)
        }
      } else if (geometry.type === 'LineString') {
        const path = (geometry.coordinates as number[][]).map(c =>
          this.map.project(c as [number, number]),
        )
        if (this.distanceToPath(px, path) < tolerance) hit = String(feature.id)
      }
    })
    return hit
  }

  private pointInRing(
    p: { x: number; y: number },
    ring: { x: number; y: number }[],
  ): boolean {
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[i]
      const b = ring[j]
      const intersects =
        a.y > p.y !== b.y > p.y &&
        p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
      if (intersects) inside = !inside
    }
    return inside
  }

  private distanceToPath(
    p: { x: number; y: number },
    path: { x: number; y: number }[],
  ): number {
    let min = Infinity
    for (let i = 1; i < path.length; i++) {
      min = Math.min(min, this.distanceToSegment(p, path[i - 1], path[i]))
    }
    return min
  }

  private distanceToSegment(
    p: { x: number; y: number },
    a: { x: number; y: number },
    b: { x: number; y: number },
  ): number {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len2 = dx * dx + dy * dy
    const t =
      len2 === 0
        ? 0
        : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2))
    const cx = a.x + t * dx
    const cy = a.y + t * dy
    return Math.hypot(p.x - cx, p.y - cy)
  }

  // The blue outline highlight is shown on BOTH the selected feature (persistent)
  // and the hovered one (transient) — the same highlight language for both.
  private refreshOutline(): void {
    const source = this.map.getSource(HOVER_SOURCE_ID) as GeoJSONSource
    if (!source) return
    const ids = new Set<string>()
    if (this.selectedId) ids.add(this.selectedId)
    if (this.hoveredId) ids.add(this.hoveredId)
    if (this.activeDragId) ids.delete(this.activeDragId) // hot overlay owns it
    const snapshot = this.draw.getSnapshot()
    const features: GeoJSON.Feature[] = []
    ids.forEach(id => {
      const feature = snapshot.find(f => String(f.id) === id)
      const meta = feature && this.featureMeta.get(feature.id)
      if (!feature || !meta) return
      const coords = this.outlineCoords(feature)
      if (!coords) return
      features.push({
        type: 'Feature',
        // width = the feature's stroke width → line-gap-width spaces the outline
        // to hug each edge of that stroke.
        properties: { width: meta.style.strokeWidth ?? 1 },
        geometry: { type: 'LineString', coordinates: coords },
      })
    })
    source.setData({ type: 'FeatureCollection', features })
    // Hover-affordance cursor is a Select-tool concern only. In Draw mode the
    // brush cursor (updateCursor) owns it — don't clobber it on a stroke commit.
    if (this.currentTool === 'select') {
      this.setCursor(this.hoveredId ? 'move' : null)
    }
  }

  private kindForCurrent(): MarkupKind {
    switch (this.currentTool) {
      case 'shape':
        return 'shape'
      case 'arrow':
        return 'arrow'
      case 'draw':
        return this.drawType === 'highlighter' ? 'highlighter' : 'freehand'
      case 'text':
        return 'text'
      case 'callout':
        return 'callout'
      default:
        return 'freehand'
    }
  }

  private effectiveStyle(): IMarkupStyle {
    if (this.currentTool === 'draw' && this.drawType === 'highlighter') {
      return { ...this.currentStyle, opacity: HIGHLIGHTER_OPACITY }
    }
    return { ...this.currentStyle }
  }

  private metaFor(feature: GeoJSONStoreFeatures): IFeatureMeta {
    const existing = this.featureMeta.get(feature.id)
    if (existing) return existing
    return { kind: this.kindForCurrent(), style: this.effectiveStyle() }
  }

  private readonly styleColor = (feature: GeoJSONStoreFeatures): HexColor =>
    this.metaFor(feature).style.color as HexColor

  private fillOf(feature: GeoJSONStoreFeatures): string {
    return (
      this.metaFor(feature).style.fillColor ?? this.metaFor(feature).style.color
    )
  }

  private readonly styleFillColor = (feature: GeoJSONStoreFeatures): HexColor =>
    splitHexAlpha(this.fillOf(feature)).color as HexColor

  private readonly styleFillOpacity = (
    feature: GeoJSONStoreFeatures,
  ): number => {
    // While multi-click drawing, hide Terra Draw's in-progress polygon fill — we
    // render the (snappable) preview ourselves; committed polygons keep TD's fill.
    if (this.previewingMultiClick && !this.featureMeta.has(feature.id)) return 0
    return splitHexAlpha(this.fillOf(feature)).opacity
  }

  private readonly styleWidth = (feature: GeoJSONStoreFeatures): number => {
    // Committed line/polygon strokes are painted by our stroke overlay (instant,
    // dash-capable, no Terra Draw render batching), so hide Terra Draw's stroke
    // for them. While multi-click drawing, also hide the in-progress stroke — we
    // render that preview ourselves so Shift can snap it. Points keep their width.
    if (
      feature.geometry.type !== 'Point' &&
      (this.featureMeta.has(feature.id) || this.previewingMultiClick)
    )
      return 0
    return this.metaFor(feature).style.strokeWidth
  }

  private vertexEditFlags() {
    // Vertices + midpoints + delete: drag a vertex to move it, click a midpoint to
    // insert a vertex there (Terra Draw inserts on left-click since `midpoints` is a
    // boolean, not `{ draggable: true }`), right-click a vertex to delete it.
    return {
      feature: {
        draggable: true,
        coordinates: { midpoints: true, draggable: true, deletable: true },
      },
    }
  }

  private moveOnlyFlags() {
    return { feature: { draggable: true } }
  }

  // Full select-mode styling for a stage. Terra Draw paints the selection
  // vertex/midpoint handles whenever a line/polygon is selected, so the
  // move-vs-edit distinction is made by point OPACITY (0 = hidden in 'move',
  // 1 = visible in 'edit'). The selected line/polygon stroke stays width 0 — our
  // overlay paints committed strokes — matching the idle style.
  private selectStyles(stage: 'move' | 'edit') {
    const handleOpacity = stage === 'edit' ? 1 : 0
    return {
      selectedLineStringWidth: this.styleWidth,
      selectedLineStringColor: this.styleColor,
      selectedPolygonOutlineWidth: this.styleWidth,
      selectedPolygonOutlineColor: this.styleColor,
      selectedPolygonColor: this.styleFillColor,
      selectedPolygonFillOpacity: this.styleFillOpacity,
      selectionPointWidth: VERTEX_RADIUS,
      selectionPointColor: HANDLE_FILL as HexColor,
      selectionPointOpacity: handleOpacity,
      selectionPointOutlineColor: HANDLE_ACCENT as HexColor,
      selectionPointOutlineWidth: VERTEX_STROKE_WIDTH,
      selectionPointOutlineOpacity: handleOpacity,
      midPointWidth: MIDPOINT_RADIUS,
      midPointColor: HANDLE_ACCENT as HexColor,
      midPointOpacity: handleOpacity,
      midPointOutlineColor: HANDLE_FILL as HexColor,
      midPointOutlineWidth: MIDPOINT_STROKE_WIDTH,
      midPointOutlineOpacity: handleOpacity,
    }
  }

  // Switch the live selection between stages. Terra Draw applies
  // updateModeOptions to the current selection immediately, so no re-select (and
  // thus no orphaned handle dots) is needed.
  private applyStage(stage: 'move' | 'edit'): void {
    const lineFlags =
      stage === 'edit' ? this.vertexEditFlags() : this.moveOnlyFlags()
    this.draw.updateModeOptions(this.modeNames.select, {
      flags: {
        [this.modeNames.line]: lineFlags,
        [this.modeNames.polygon]: lineFlags,
        [this.modeNames.rectangle]: this.moveOnlyFlags(),
        [this.modeNames.circle]: this.moveOnlyFlags(),
        [this.modeNames.freehand]: this.moveOnlyFlags(),
        [this.modeNames.point]: this.moveOnlyFlags(),
      },
      styles: this.selectStyles(stage),
    })
  }

  // Vertex-editable = a line-family feature (line/arrow/polyline) or a polygon.
  private isVertexEditable(id: string): boolean {
    const meta = this.featureMeta.get(id)
    if (!meta) return false
    return meta.kind === 'arrow' || meta.shapeKind === 'polygon'
  }

  // Second click of the already-selected line/polygon advances it to vertex edit.
  // Deferred a tick: Terra Draw's synchronous select sets `freshSelection`, so
  // the click that just selected a feature is consumed here; a click on the
  // already-selected feature instead advances the stage.
  private readonly onSelectClick = (e: MapMouseEvent): void => {
    if (this.currentTool !== 'select') return
    this.defer(() => {
      if (this.freshSelection) {
        this.freshSelection = false
        return
      }
      if (this.selectionStage !== 'move' || this.selectedId == null) return
      if (!this.isVertexEditable(this.selectedId)) return
      if (this.hitTest(e.point) !== this.selectedId) return
      this.advanceToVertexEdit()
      this.options.onEnterVertexEdit?.()
    })
  }

  // iOS fires the selecting tap as pointer-only (no click), so onSelectClick never
  // consumes `freshSelection` on it — without this, the next tap is wasted consuming
  // the stale flag and a third is needed to advance. Clearing it on the next
  // pointerdown (which belongs to the tap AFTER the select; the selecting tap's own
  // pointerdown fires before onSelect sets the flag) lets that tap advance. Desktop is
  // unaffected: there the selecting tap's own click already consumed it, so this no-ops.
  private readonly onSelectPointerDown = (): void => {
    if (this.currentTool !== 'select') return
    if (this.freshSelection) this.freshSelection = false
  }

  private advanceToVertexEdit(): void {
    this.selectionStage = 'edit'
    this.applyStage('edit')
    this.reselectCurrent()
    this.refreshHandles()
    this.refreshOutline()
  }

  // True for a selected 2-point Line/Arrow (1D): it shows endpoints, not a box,
  // and lives entirely in the 'edit' stage (no move stage). Adding a vertex via a
  // midpoint promotes it to a polyline (3+ coords), where this turns false.
  private isTwoPointLine(id: string | null): boolean {
    if (id == null) return false
    const feature = this.snapshotFeature(id)
    return (
      feature?.geometry.type === 'LineString' &&
      (feature.geometry.coordinates as number[][]).length === 2
    )
  }

  // Esc step-down: 'edit' → 'move' (hide handles, keep selected) returns true;
  // otherwise (already 'move', or a box-less 2-point line) returns false so the
  // caller deselects.
  public stepDownToMove(): boolean {
    if (this.selectionStage !== 'edit') return false
    if (this.isTwoPointLine(this.selectedId)) return false
    this.selectionStage = 'move'
    this.applyStage('move')
    this.reselectCurrent()
    this.refreshHandles()
    this.refreshOutline()
    return true
  }

  // Terra Draw builds a selected feature's vertex/midpoint coordinate points
  // once, at select time, from the flags then in effect — updateModeOptions does
  // not retroactively add or remove them. So after a stage's flags change, force
  // a deselect→reselect to regenerate the points for the new stage (none in
  // 'move', vertices + midpoints in 'edit'). Guarded so the events don't reset
  // the selection bookkeeping.
  private reselectCurrent(): void {
    const id = this.selectedId
    if (id == null || !this.draw.hasFeature(id)) return
    this.reapplying = true
    try {
      this.draw.deselectFeature(id)
      this.draw.selectFeature(id, this.modeNames.select)
    } finally {
      this.reapplying = false
    }
  }

  private readonly onFinish = (id: FeatureId): void => {
    if (this.currentTool === 'select' || this.isEraser) return
    const lineType = this.currentTool === 'arrow' ? this.lineType : undefined
    const heads = this.defaultArrowheads(lineType)
    this.featureMeta.set(id, {
      kind: this.kindForCurrent(),
      style: this.effectiveStyle(),
      shapeKind: this.currentTool === 'shape' ? this.shapeKind : undefined,
      lineType,
      arrowheadStart: this.currentTool === 'arrow' ? heads.start : undefined,
      arrowheadEnd: this.currentTool === 'arrow' ? heads.end : undefined,
      bboxAngle: this.map.getBearing(),
      globeViewId: this.options.getGlobeViewId?.(),
    })
    this.refreshArrowheads()
    this.options.onChange?.()
    // A closed polygon (the only thing that reaches onFinish from the Shape tool)
    // behaves like rect/circle: switch to Select and select the new shape. Defer
    // past Terra Draw's finish dispatch before re-entering setMode/selectFeature.
    if (this.currentTool === 'shape') {
      // Commit our own buffer ring so Shift-snapped vertices win over Terra Draw's
      // raw ones. Terra Draw's finish fires before the closing click is mirrored
      // into the buffer, so the buffer is exactly the placed vertices — close it
      // as-is (the polyline close does the same). No-op when not snapping (the
      // buffer matches Terra Draw's coordinates).
      if (this.polygonPoints.length >= 3) {
        this.draw.updateFeatureGeometry(id, {
          type: 'Polygon',
          coordinates: [
            this.roundCoords([...this.polygonPoints, this.polygonPoints[0]]),
          ],
        })
      }
      this.resetPolygonPoints()
      this.defer(() => this.finishDiscreteDraw(id))
    } else if (this.currentTool === 'arrow') {
      // Polyline finished (the only arrow that reaches Terra Draw's finish) —
      // clear the draw-time vertex dots.
      this.resetPolylinePoints()
    }
  }

  private readonly onChange = (): void => {
    this.refreshArrowheads()
    this.refreshDrawVertices()
    this.options.onChange?.()
  }

  private readonly onSelect = (id: FeatureId): void => {
    // A stage-change reselect re-fires this event; keep the existing selection
    // bookkeeping (selectedId, stage, style) untouched in that case.
    if (this.reapplying) return
    this.selectedId = String(id)
    // Adopt the selected feature's style as the working style, so editing one
    // property (restyleSelected copies the whole working style) doesn't clobber
    // the feature's other properties with stale last-used values.
    const meta = this.featureMeta.get(id)
    if (meta) this.currentStyle = { ...this.currentStyle, ...meta.style }
    // The hover outline now becomes the selection outline (one and the same).
    if (this.hoveredId === this.selectedId) this.hoveredId = null
    // Every fresh selection starts in 'move' (handles hidden); reset the flags in
    // case the previous selection had advanced to 'edit'. `freshSelection` lets
    // onSelectClick tell this selecting click apart from a 2nd (advance) click.
    this.selectionStage = 'move'
    this.freshSelection = true
    this.applyStage('move')
    this.refreshHandles()
    this.refreshOutline()
    this.options.onSelectionChange?.(this.selectedId)
    // A 2-point Line/Arrow is 1D (no box) — show its endpoints + midpoint straight
    // away (Excalidraw-style) by auto-advancing to the vertex-edit stage. Deferred
    // like onSelectClick so Terra Draw rebuilds the vertex/midpoint points with the
    // edit flags (a plain flag change doesn't regenerate them).
    if (this.isTwoPointLine(this.selectedId)) {
      const id = this.selectedId
      this.defer(() => {
        if (this.selectedId === id && this.selectionStage === 'move') {
          this.advanceToVertexEdit()
        }
      })
    }
  }

  private readonly onDeselect = (): void => {
    // The deselect half of a stage-change reselect: ignore it, the reselect that
    // follows keeps the same feature selected.
    if (this.reapplying) return
    this.selectedId = null
    this.selectionStage = 'move'
    // Reset the select mode back to move-only flags so the NEXT fresh selection
    // generates no vertex points (Terra Draw builds them from the flags active at
    // select time — leaving 'edit' flags here would leak handles onto it).
    this.applyStage('move')
    this.refreshHandles()
    this.refreshOutline()
    this.options.onSelectionChange?.(null)
  }

  private removeFeature(id: FeatureId): void {
    if (!this.draw.hasFeature(id)) return
    this.draw.removeFeatures([id])
    this.featureMeta.delete(id)
    if (this.selectedId === String(id)) this.onDeselect()
    this.options.onChange?.()
  }

  private restyleSelected(): void {
    if (this.selectedId == null) return
    const feature = this.snapshotFeature(this.selectedId)
    const meta = feature && this.featureMeta.get(feature.id)
    if (!feature || !meta) return
    this.featureMeta.set(feature.id, {
      ...meta,
      style: {
        ...meta.style,
        color: this.currentStyle.color,
        strokeWidth: this.currentStyle.strokeWidth,
        strokeStyle: this.currentStyle.strokeStyle,
        fillColor: this.currentStyle.fillColor,
        fillStyle: this.currentStyle.fillStyle,
      },
    })
    // Nudge Terra Draw to re-run the style callbacks (which read featureMeta)
    // via an in-place property update — NOT removeFeatures/addFeatures, which
    // would deselect the feature and tear down the open color picker. Throttled
    // (see scheduleRestyleFlush) so a slider drag actually paints mid-drag.
    this.scheduleRestyleFlush(String(feature.id))
    this.refreshArrowheads()
    this.options.onChange?.()
  }

  // Terra Draw's mapbox adapter coalesces renders on a self-cancelling rAF, so a
  // burst of updateFeatureProperties (a width-slider drag) keeps cancelling the
  // pending frame and never paints until the drag stops. Throttling the nudge
  // leaves frame gaps for the adapter to render → near real-time restyle. The
  // featureMeta + dashed overlay already updated synchronously above, so the
  // flush always renders the latest style; the trailing call lands the final.
  private scheduleRestyleFlush(id: string): void {
    if (this.restyleTimer) return
    this.restyleTimer = setTimeout(() => {
      this.restyleTimer = null
      if (!this.draw.hasFeature(id)) return
      this.restyleNonce += 1
      this.draw.updateFeatureProperties(id, { restyleNonce: this.restyleNonce })
    }, 32)
  }

  private toMarkupData(feature: GeoJSONStoreFeatures): IMarkupFeatureData {
    const meta = this.featureMeta.get(feature.id)
    const geometry = feature.geometry
    let ring: number[][] = []
    if (geometry.type === 'Polygon')
      ring = geometry.coordinates[0] as number[][]
    else if (geometry.type === 'LineString')
      ring = geometry.coordinates as number[][]
    else if (geometry.type === 'Point')
      ring = [geometry.coordinates as number[]]
    return {
      id: String(feature.id),
      kind: meta.kind,
      geometryType: geometry.type,
      coordinates: ring.map(([longitude, latitude]) => ({
        longitude,
        latitude,
      })),
      color: meta.style.color,
      strokeWidth: meta.style.strokeWidth,
      opacity: meta.style.opacity,
      strokeStyle: meta.style.strokeStyle,
      fillColor: meta.style.fillColor,
      fillStyle: meta.style.fillStyle,
      text: meta.text,
      shapeKind: meta.shapeKind,
      lineType: meta.lineType,
      arrowheadStart: meta.arrowheadStart,
      arrowheadEnd: meta.arrowheadEnd,
      rotation: meta.bboxAngle || undefined,
      globeViewId: meta.globeViewId,
    }
  }

  private toFeature(markup: IMarkupFeatureData): GeoJSONStoreFeatures {
    const points = markup.coordinates.map(c => [c.longitude, c.latitude])
    const mode = this.modeForKind(markup)
    const geometry =
      markup.geometryType === 'Polygon'
        ? { type: 'Polygon' as const, coordinates: [points] }
        : markup.geometryType === 'Point'
          ? { type: 'Point' as const, coordinates: points[0] }
          : { type: 'LineString' as const, coordinates: points }
    return {
      id: markup.id,
      type: 'Feature',
      properties: { mode },
      geometry,
    } as GeoJSONStoreFeatures
  }

  private modeForKind(markup: IMarkupFeatureData): string {
    switch (markup.kind) {
      case 'shape':
        return this.modeNames[markup.shapeKind ?? 'polygon']
      case 'arrow':
        return this.modeNames.line
      case 'text':
      case 'callout':
        return this.modeNames.point
      default:
        return this.modeNames.freehand
    }
  }
}
